// src/score_performance/assessment/assessment.ts
import { TieState } from "score_data";
import { MusicTextures, NoteheadType } from "score_render";
var Assessor = class {
  constructor(scoreViewer, visualizer) {
    this._totalCorrect = 0;
    this._totalIncorrect = 0;
    // ignore: unused_field
    this._totalExtra = 0;
    this._totalBeatsCorrect = 0;
    this._totalBeatsIncorrect = 0;
    this._totalBeatsExtra = 0;
    /**a list of ints indicating hits (0), misses (1) and extra notes (2)*/
    this.history = [];
    this._scoreViewer = scoreViewer;
    this._visualizer = visualizer;
    this._init();
  }
  _init() {
    this.setVisualizer(this._visualizer ?? new NewAssessmentVisualizer());
    this._createAssessmentNotes();
    this._createAssessmentBeats();
  }
  setVisualizer(visualizer) {
    visualizer.assessor = this;
    this._visualizer = visualizer;
  }
  prepareForRemoval() {
    this.stop();
    this._visualizer.prepareForRemoval();
    this._visualizer = null;
    this._scoreViewer = null;
    this._noteList = null;
    this._ngToAssessNoteMap = null;
    this._vngToAssessNoteMap = null;
    this._ngToVngMap = null;
    this._beatList = null;
    this.history = null;
  }
  reset() {
    this.clearAssessmentVisuals();
    this._totalCorrect = 0;
    this._totalIncorrect = 0;
    this._totalExtra = 0;
    this._totalBeatsCorrect = 0;
    this._totalBeatsIncorrect = 0;
    this._totalBeatsExtra = 0;
    this.history = [];
  }
  /**removes extra red notes and sets red/green notes back to black*/
  clearAssessmentVisuals() {
    this._visualizer.clearAssessmentVisuals();
    this.scoreViewer.pixiAppHelper.requestRender();
  }
  /**returns score for assessment performance, from 0 to 100*/
  getScore() {
    let numNotes = this._noteList.length;
    let extraPenalty = this._totalExtra - (numNotes - this._totalCorrect);
    if (extraPenalty < 0)
      extraPenalty = 0;
    let score = (this._totalCorrect - extraPenalty) / numNotes;
    if (score < 0)
      score = 0;
    return Math.floor(score * 100);
  }
  /**returns score for beat assessment performance, from 0 to 100*/
  getBeatScore() {
    let score = this._totalBeatsCorrect / this._beatList.length * 100;
    return Math.floor(score);
  }
  getResults() {
    let results = new AssessmentResults();
    results.numNotes = this._noteList.length;
    results.notesCorrect = this._totalCorrect;
    results.notesIncorrect = this._noteList.length - this._totalCorrect;
    results.notesExtra = this._totalExtra;
    results.history = this.history;
    results.percentage = this.getScore();
    results.numBeats = this._beatList.length;
    results.beatsCorrect = this._totalBeatsCorrect;
    results.beatsIncorrect = this._totalBeatsIncorrect;
    results.beatsExtra = this._totalBeatsCorrect;
    results.beatsPercentage = this.getBeatScore();
    return results;
  }
  /**returns the [AssessmentNote] that corresponds to the requested
   * NoteGroup*/
  getAssessNoteFromNg(ng) {
    return this._ngToAssessNoteMap.get(ng);
  }
  /**returns the [AssessmentNote] that corresponds to the requested 
   * VisualNoteGroup*/
  getAssessNoteFromVNG(vng) {
    return this._vngToAssessNoteMap.get(vng);
  }
  /**returns the [VisualNoteGroup] that corresponds to the requested
   * NoteGroup*/
  getVngFromNg(ng) {
    return this._ngToVngMap.get(ng);
  }
  /**override in subclasses to handle instrument changes*/
  setInstrument(inst) {
  }
  _createAssessmentNotes() {
    this._ngToAssessNoteMap = /* @__PURE__ */ new Map();
    this._vngToAssessNoteMap = /* @__PURE__ */ new Map();
    this._ngToVngMap = this._scoreViewer.vScore.getNgToVngMap();
    this._noteList = [];
    for (let vng of this._scoreViewer.vngList) {
      let ng = vng.noteGroup;
      if (!ng.isRest && (ng.notes[0].tieState === TieState.NONE || ng.notes[0].tieState === TieState.START)) {
        let note = new AssessmentNote();
        note.vng = vng;
        this._noteList.push(note);
        this._ngToAssessNoteMap.set(note.vng.noteGroup, note);
        this._vngToAssessNoteMap.set(note.vng, note);
      }
    }
  }
  _createAssessmentBeats() {
    let beats = this._scoreViewer.vScore.score.getBeatQNotePositions();
    this._beatList = [];
    for (let qnTime of beats) {
      let beat = new AssessmentBeat();
      beat.qNoteTime = qnTime;
      this._beatList.push(beat);
    }
  }
  _markNoteCorrect(note) {
    note.matched = true;
    this._totalCorrect++;
    this.history.push(0);
    this._visualizer.markNoteCorrect(note);
  }
  _markNoteIncorrect(note) {
    note.matched = false;
    this._totalIncorrect++;
    this.history.push(1);
    this._visualizer.markNoteIncorrect(note);
  }
  /**adds extra performed note to display and tallies it*/
  _addExtraNote(qnTime) {
    this._totalExtra++;
    this.history.push(2);
    this._visualizer.addExtraNote(qnTime);
  }
  _markBeatCorrect(beat) {
    beat.matched = true;
    this._totalBeatsCorrect++;
    this._visualizer.markBeatCorrect(beat);
  }
  _markBeatIncorrect(beat) {
    beat.matched = false;
    this._totalBeatsIncorrect++;
    this._visualizer.markBeatIncorrect(beat);
  }
  _addExtraBeat(qnTime) {
  }
  /**override in subclasses to specify whether microphone is required. If
   * true, MicManager's startCapture will be called independently, so you
   * don't need to call it in your Assessor subclass.*/
  get requiresMicrophone() {
    throw "requiresMicrophone must be overridden";
  }
  get scoreViewer() {
    return this._scoreViewer;
  }
};
var AssessmentResults = class {
  getLongestStreak() {
    let max = 0;
    let current = 0;
    for (let i = 0; i < history.length; i++) {
      if (history[i] === 0) {
        current++;
        if (current > max) {
          max = current;
        }
      } else {
        current = 0;
      }
    }
    return max;
  }
};
var AssessmentVisualizerBase = class {
  /**set by Abstract [Assessor] class.*/
  get assessor() {
    return this._assessor;
  }
  set assessor(value) {
    this._assessor = value;
  }
  constructor() {
  }
  markNoteCorrect(note) {
  }
  markNoteIncorrect(note) {
  }
  /**places a notehead on a system indicating an extra performed note. The
   * [qnTime] is the quarter note time in the score where the extra note 
   * should be placed.*/
  addExtraNote(qnTime) {
  }
  markBeatCorrect(beat) {
  }
  markBeatIncorrect(beat) {
  }
  /**resets visuals for new assessment*/
  clearAssessmentVisuals() {
  }
  prepareForRemoval() {
  }
};
var _AssessmentVisualizer = class _AssessmentVisualizer extends AssessmentVisualizerBase {
  constructor() {
    super();
  }
  markNoteCorrect(note) {
    note.vng.setColor(_AssessmentVisualizer.GREEN);
    this._assessor.scoreViewer.vScore.pixiAppHelper.requestRender();
  }
  markNoteIncorrect(note) {
    note.vng.setColor(_AssessmentVisualizer.RED);
    this._assessor.scoreViewer.vScore.pixiAppHelper.requestRender();
  }
  /**places a notehead on a system indicating an extra performed note*/
  addExtraNote(qnTime) {
    let xVal = 0;
    let yVal = 0;
    let vngList = this._assessor.scoreViewer.vngList;
    let numNotes = vngList.length;
    let targetSystem;
    if (vngList[0].noteGroup.qNoteTime > qnTime) {
      xVal = vngList[0].originalPosition.x - 30;
      targetSystem = vngList[0].vSystemRef;
    } else if (vngList[vngList.length - 1].noteGroup.qNoteTime < qnTime) {
      xVal = vngList[vngList.length - 1].originalPosition.x + 30;
      targetSystem = vngList[vngList.length - 1].vSystemRef;
    } else {
      let i = numNotes - 1;
      while (i >= 0) {
        let cng = vngList[i].noteGroup;
        if (cng.qNoteTime <= qnTime) {
          let noteBefore = vngList[i];
          let noteAfter = i < numNotes - 1 ? vngList[i + 1] : noteBefore;
          if (noteAfter.originalPosition.x >= noteBefore.originalPosition.x) {
            let posRatio = (qnTime - noteBefore.noteGroup.qNoteTime) / (noteAfter.noteGroup.qNoteTime - noteBefore.noteGroup.qNoteTime);
            xVal = noteBefore.originalPosition.x + posRatio * (noteAfter.originalPosition.x - noteBefore.originalPosition.x);
          } else {
            xVal = noteBefore.originalPosition.x + 30;
          }
          targetSystem = noteBefore.vSystemRef;
          break;
        }
        i--;
      }
      targetSystem.addExtraNotehead(xVal, yVal, _AssessmentVisualizer.RED);
      this._assessor.scoreViewer.vScore.pixiAppHelper.requestRender();
    }
  }
  /**removes extra red notes and sets red/green notes back to black*/
  clearAssessmentVisuals() {
    for (let vng of this._assessor.scoreViewer.vngList) {
      vng.setColor(_AssessmentVisualizer.BLACK);
    }
    let systems = this._assessor.scoreViewer.vScore.getVisualSystems();
    for (let sys of systems) {
      sys.removeExtraNoteheads();
    }
    this._assessor.scoreViewer.vScore.pixiAppHelper.requestRender();
  }
  prepareForRemoval() {
  }
};
_AssessmentVisualizer.GREEN = 65280;
_AssessmentVisualizer.RED = 16711680;
_AssessmentVisualizer.BLACK = 0;
var AssessmentVisualizer = _AssessmentVisualizer;
var _NewAssessmentVisualizer = class _NewAssessmentVisualizer extends AssessmentVisualizerBase {
  constructor() {
    super();
    this.autoMarkTiedNotes = true;
  }
  markNoteCorrect(note) {
    this.setNoteColor(note.vng, _NewAssessmentVisualizer.GREEN);
    if (this.autoMarkTiedNotes) {
      this.markTiedNotes(note, _NewAssessmentVisualizer.GREEN);
    }
    this._assessor.scoreViewer.vScore.pixiAppHelper.requestRender();
  }
  markNoteIncorrect(note) {
    this.setNoteColor(note.vng, _NewAssessmentVisualizer.RED);
    if (this.autoMarkTiedNotes) {
      this.markTiedNotes(note, _NewAssessmentVisualizer.RED);
    }
    this._assessor.scoreViewer.vScore.pixiAppHelper.requestRender();
  }
  /**places a notehead on a system indicating an extra performed note*/
  addExtraNote(qnTime) {
    let xVal = 0;
    let yVal = 0;
    let vngList = this._assessor.scoreViewer.vngList;
    let numNotes = vngList.length;
    let targetSystem;
    if (vngList[0].noteGroup.qNoteTime > qnTime) {
      xVal = vngList[0].originalPosition.x - 30;
      targetSystem = vngList[0].vSystemRef;
    } else if (vngList[vngList.length - 1].noteGroup.qNoteTime < qnTime) {
      xVal = vngList[vngList.length - 1].originalPosition.x + 30;
      targetSystem = vngList[vngList.length - 1].vSystemRef;
    } else {
      let i = numNotes - 1;
      while (i >= 0) {
        let cng = vngList[i].noteGroup;
        if (cng.qNoteTime <= qnTime) {
          let noteBefore = vngList[i];
          let noteAfter = i < numNotes - 1 ? vngList[i + 1] : noteBefore;
          if (noteAfter.originalPosition.x >= noteBefore.originalPosition.x) {
            let posRatio = (qnTime - noteBefore.noteGroup.qNoteTime) / (noteAfter.noteGroup.qNoteTime - noteBefore.noteGroup.qNoteTime);
            xVal = noteBefore.originalPosition.x + posRatio * (noteAfter.originalPosition.x - noteBefore.originalPosition.x);
          } else {
            xVal = noteBefore.originalPosition.x + 30;
          }
          targetSystem = noteBefore.vSystemRef;
          break;
        }
        i--;
      }
    }
    let props = this._assessor.scoreViewer.vScore.score.scoreProperties;
    let textures = MusicTextures.getTextureSet(props.pixelsPerSpace);
    let sprite = textures.getColoredNotehead(
      NoteheadType.QUARTER,
      _NewAssessmentVisualizer.RED
    );
    sprite.x = xVal + targetSystem.scoreX;
    sprite.y = yVal + targetSystem.scoreY;
    this._assessor.scoreViewer.assessmentOverlay.addChild(sprite);
    this._assessor.scoreViewer.vScore.pixiAppHelper.requestRender();
  }
  /**removes extra red notes and sets red/green notes back to black*/
  clearAssessmentVisuals() {
    this._assessor._scoreViewer.assessmentOverlay.removeChildren(0);
    this._assessor.scoreViewer.vScore.pixiAppHelper.requestRender();
  }
  setNoteColor(vng, color) {
    let props = this._assessor.scoreViewer.vScore.score.scoreProperties;
    let textures = MusicTextures.getTextureSet(props.pixelsPerSpace);
    for (let nh of vng.noteheadList) {
      let sprite = textures.getColoredNotehead(vng.noteheadType, color);
      sprite.x = vng.scoreX + nh.x;
      sprite.y = vng.scoreY + nh.y;
      this._assessor.scoreViewer.assessmentOverlay.addChild(sprite);
    }
  }
  markTiedNotes(note, color) {
    let ng = note.vng.noteGroup;
    let tieState = ng.notes[0].tieState;
    while (tieState === TieState.START || tieState === TieState.CONTINUE) {
      let nextNg = ng.next;
      if (nextNg.isRest) {
        break;
      }
      this.setNoteColor(this._assessor.getVngFromNg(nextNg), color);
      ng = nextNg;
      tieState = ng.notes[0].tieState;
    }
  }
  prepareForRemoval() {
    this.clearAssessmentVisuals();
  }
};
_NewAssessmentVisualizer.GREEN = 65280;
_NewAssessmentVisualizer.RED = 16711680;
var NewAssessmentVisualizer = _NewAssessmentVisualizer;
var AssessmentNote = class {
  constructor() {
    this.matched = false;
  }
};
var AssessmentBeat = class {
  constructor() {
    this.matched = false;
  }
};

// src/score_performance/assessment/AssessmentType.ts
var AssessmentType = /* @__PURE__ */ ((AssessmentType2) => {
  AssessmentType2[AssessmentType2["none"] = 0] = "none";
  AssessmentType2[AssessmentType2["rhythmKeyboard"] = 1] = "rhythmKeyboard";
  AssessmentType2[AssessmentType2["rhythmClap"] = 2] = "rhythmClap";
  AssessmentType2[AssessmentType2["pitch"] = 3] = "pitch";
  AssessmentType2[AssessmentType2["rhythmKeyboardFreetime"] = 4] = "rhythmKeyboardFreetime";
  return AssessmentType2;
})(AssessmentType || {});

// src/score_performance/assessment/FreeTimeRhythmAssessor.ts
import { HitType, KeyHitRecognizer } from "audio_input";
import { NoteInfoDO, SoundLibManager } from "sound_lib";
import { ScoreProperties } from "score_data";
import { createEmitter } from "ts_utils";
var FreeTimeRhythmAssessor = class extends Assessor {
  constructor(scoreViewer) {
    super(scoreViewer);
    this._defaultWindowSize = 0.25;
    this._tempo = ScoreProperties.DEFAULT_TEMPO;
    /**with [_dynamicTempo] the current tempo will be updated with each hit*/
    this._dynamicTempo = false;
    this._isActive = false;
    this._assessmentCompleteEmitter = createEmitter();
    this._onHit = (e) => {
      if (e.hitType === HitType.rhythm_hit_start) {
        if (!this._dynamicTempo) {
          this._handleRhythmHit(e);
        } else {
          this._handleRhythmHitDynamicTempo(e);
        }
      }
    };
    this._initialize();
  }
  /**Dispatches whenever assessment completes. Value is the user's score.*/
  get assessmentComplete() {
    return this._assessmentCompleteEmitter.event;
  }
  /**starts assessment*/
  start() {
    this._noteIndex = 0;
    this._lastHitTime = null;
    this._hitRecognizer.startHitRec();
    this._hitRecognizer.event.on(this._onHit);
    this._isActive = true;
  }
  /**stops assessment*/
  stop() {
    if (this._isActive) {
      this._hitRecognizer.stopHitRec();
      this._hitRecognizer.event.off(this._onHit);
      this._isActive = false;
    }
  }
  //change this if clap support added
  get requiresMicrophone() {
    return false;
  }
  _initialize() {
    this._slm = SoundLibManager.get();
    this._hitRecognizer = KeyHitRecognizer.get(this._slm.context);
    this._hitRecognizer.sendBeatEvents = false;
  }
  _handleRhythmHit(e) {
    if (this._noteIndex >= this._noteList.length) {
      return;
    }
    let note = this._noteList[this._noteIndex];
    if (this._noteIndex === 0) {
      this._markNoteCorrect(note);
    } else {
      let cng = this._noteList[this._noteIndex].vng.noteGroup;
      let png = this._noteList[this._noteIndex - 1].vng.noteGroup;
      let qNoteDelta = cng.qNoteTime - png.qNoteTime;
      let goalTime = qNoteDelta * 60 / this._tempo + this._lastHitTime;
      let minTime = goalTime - 60 / this._tempo * this._defaultWindowSize;
      let maxTime = goalTime + 60 / this._tempo * this._defaultWindowSize;
      if (e.time >= minTime && e.time <= maxTime) {
        this._markNoteCorrect(note);
      } else {
        this._markNoteIncorrect(note);
      }
    }
    this._playNote(note.vng.noteGroup.notes[0]);
    this._lastHitTime = e.time;
    this._noteIndex++;
    if (this._noteIndex === this._noteList.length) {
      this._completeAssessment();
    }
  }
  _handleRhythmHitDynamicTempo(e) {
    if (this._noteIndex >= this._noteList.length) {
      return;
    }
    let note = this._noteList[this._noteIndex];
    if (this._noteIndex === 0) {
      this._markNoteCorrect(note);
    } else {
      let cng = this._noteList[this._noteIndex].vng.noteGroup;
      let png = this._noteList[this._noteIndex - 1].vng.noteGroup;
      let qNoteDelta = cng.qNoteTime - png.qNoteTime;
      let goalTime = qNoteDelta * 60 / this._tempo + this._lastHitTime;
      let minTime = goalTime - 60 / this._tempo * this._defaultWindowSize;
      let maxTime = goalTime + 60 / this._tempo * this._defaultWindowSize;
      if (this._noteIndex === 1) {
        this._markNoteCorrect(note);
      } else if (e.time >= minTime && e.time <= maxTime) {
        this._markNoteCorrect(note);
      } else {
        this._markNoteIncorrect(note);
      }
      let deltaTime = e.time - this._lastHitTime;
      this._tempo = qNoteDelta / deltaTime * 60;
    }
    this._playNote(note.vng.noteGroup.notes[0]);
    this._lastHitTime = e.time;
    this._noteIndex++;
    if (this._noteIndex === this._noteList.length) {
      this._completeAssessment();
    }
  }
  _playNote(note) {
    if (!this._slm.hasPreset(0, 0)) {
      throw "Preset 0:0 not loaded!";
    }
    let part = note.noteGroup.voice.measure.staff.partRef;
    let pitch = ~~(note.displayCents / 100) - part.chromaticTransposition;
    let startTime = 0;
    let duration = note.noteGroup.qNoteDuration * 60 / this._tempo;
    this._slm.setPreset(0, 0);
    this._slm.playNotes([NoteInfoDO.create(
      pitch,
      startTime,
      duration,
      0.5,
      0.5
    )]);
  }
  _completeAssessment() {
    stop();
    this._assessmentCompleteEmitter.emit(this.getScore());
  }
  /**gets/sets the tempo in BPM*/
  get tempo() {
    return this._tempo;
  }
  set tempo(value) {
    this._tempo = value;
  }
  /**With dynamicTempo the currentTempo will constantly be recalculated as
   * each note is played. */
  get dynamicTempo() {
    return this._dynamicTempo;
  }
  set dynamicTempo(value) {
    this._dynamicTempo = value;
  }
};

// src/score_performance/assessment/PitchAssessor.ts
import {
  AssessmentInstrumentUtils,
  MediaDeviceManager,
  MicManager,
  PitchRecognizer
} from "audio_input";
import { SoundLibManager as SoundLibManager2 } from "sound_lib";

// src/score_performance/scoreViewer/ScoreViewer.ts
import { Container as Container2, Graphics as Graphics2 } from "pixi.js";
import { createEmitter as createEmitter3 } from "ts_utils";

// src/score_performance/scoreViewer/ScoreViewerInteractionManager.ts
import { Point } from "pixi.js";
import {
  PointDetails
} from "score_render";

// src/score_performance/scoreViewer/InteractionSettings.ts
var InteractionSettings = class {
  constructor() {
    this.enableScrollWheel = false;
    this.enableDragScroll = false;
    this.enableMeasureClickEvents = false;
    this.enableNoteClickEvents = false;
    this.enableRegionClickEvents = false;
    this.enableMeasureOverEvents = false;
    this.enableNoteOverEvents = false;
    this.enableRegionOverEvents = false;
    /**if the ScoreViewer doesn't move around the */
    this.scoreViewerIsStationary = true;
  }
  get _requiresMouseUpDown() {
    return this.enableDragScroll || this.enableMeasureClickEvents || this.enableNoteClickEvents;
  }
  get _requiresMouseMove() {
    return this.enableDragScroll || this.enableMeasureOverEvents || this.enableNoteOverEvents;
  }
  get _requiresWheel() {
    return this.enableScrollWheel;
  }
};

// src/score_performance/scoreViewer/ScoreViewerInteractionManager.ts
var ScoreViewerInteractionManager = class {
  constructor(viewManager, scoreViewer) {
    this._enabled = false;
    this._mouseDownPotentialClick = false;
    this._minDeltaForDragScroll = 10;
    this._maxDeltaForClick = 5;
    this._lastScrollWheelTime = 0;
    this._spaceAboveScore = 0;
    this._spaceBelowScore = 100;
    this._onMouseDown = (e) => {
      this._getMousePositionDetails(e);
      if (this._mouseDetails.mouseOutsideViewer) {
        return;
      }
      this._mouseDownPotentialClick = true;
      this._mouseDownStartPos = this._mouseDetails.clientMousePos;
      if (this._settings.enableDragScroll) {
        this._pah.app.ticker.add(this._onTick);
      }
    };
    this._onMouseMove = (e) => {
      this._getMousePositionDetails(e);
      if (!this._mouseDetails.mouseOutsideViewer) {
        this._mouseDownLastPos = this._mouseDetails.clientMousePos;
        if (this._settings.enableMeasureOverEvents || this._settings.enableNoteOverEvents || this._settings.enableRegionOverEvents) {
          this._getPointDetailsInScore();
          let vMeas = this._pointDetails.visualMeasure;
          if (vMeas && this._lastMeasureOver != vMeas) {
            let ev = new ScoreViewerEvent(
              3 /* measureMouseOver */
            );
            ev.visualMeasure = vMeas;
            this._scoreViewer._dispatchClientEvent(ev);
          }
          this._lastMeasureOver = vMeas;
          let vng = this._pointDetails.visualNoteGroup;
          if (vng && this._lastNoteGroupOver != vng) {
            let ev = new ScoreViewerEvent(
              5 /* noteGroupMouseOver */
            );
            ev.visualNoteGroup = vng;
            this._scoreViewer._dispatchClientEvent(ev);
          }
          this._lastNoteGroupOver = vng;
          let region = this._pointDetails.clickRegion;
          if (region && this._lastRegionOver != region) {
            let ev = new ScoreViewerEvent(
              7 /* regionOver */
            );
            ev.clickRegion = region;
            this._scoreViewer._dispatchClientEvent(ev);
          }
          this._lastRegionOver = region;
        }
      }
    };
    this._onTick = () => {
      if (this._mouseDownLastPos == null) {
        return;
      }
      let origY = this._mouseDownStartPos.y;
      let nowY = this._mouseDownLastPos.y;
      let delta = Math.abs(nowY - origY);
      if (this._mouseDownPotentialClick && delta > this._maxDeltaForClick) {
        this._mouseDownPotentialClick = false;
      }
      if (delta < this._minDeltaForDragScroll) {
        return;
      }
      const speedFactor = 0.4;
      if (nowY > origY) {
        let inc = nowY - origY - this._minDeltaForDragScroll;
        this._scoreViewer.vScore.y -= inc * speedFactor;
      } else {
        let dec = origY - nowY - this._minDeltaForDragScroll;
        this._scoreViewer.vScore.y += dec * speedFactor;
      }
      this._fixVerticalScorePosition();
      this._vm.updateVisibleSystems();
    };
    this._onMouseUp = (e) => {
      this._getMousePositionDetails(e);
      let isClick = !this._mouseDetails.mouseOutsideViewer && this._mouseDownPotentialClick;
      this._cancelMouseDownWatch();
      if (isClick && (this._settings.enableMeasureClickEvents || this._settings.enableNoteClickEvents || this._settings.enableRegionClickEvents)) {
        this._getPointDetailsInScore();
        if (this._pointDetails.visualMeasure) {
          let ev = new ScoreViewerEvent(
            2 /* measureClicked */
          );
          ev.visualMeasure = this._pointDetails.visualMeasure;
          this._scoreViewer._dispatchClientEvent(ev);
        }
        if (this._pointDetails.visualNoteGroup) {
          let ev = new ScoreViewerEvent(
            4 /* noteGroupClicked */
          );
          ev.visualNoteGroup = this._pointDetails.visualNoteGroup;
          this._scoreViewer._dispatchClientEvent(ev);
        }
        if (this._pointDetails.clickRegion) {
          let ev = new ScoreViewerEvent(
            6 /* regionClicked */
          );
          ev.clickRegion = this._pointDetails.clickRegion;
          this._scoreViewer._dispatchClientEvent(ev);
        }
      }
    };
    this._onWheel = (e) => {
      let time = performance.now();
      if (time - this._lastScrollWheelTime < 10) {
        return;
      }
      this._lastScrollWheelTime = time;
      let direction = e.deltaY > 0 ? -1 : 1;
      const magnitude = 50;
      this._scoreViewer.vScore.y += 50 * direction;
      this._fixVerticalScorePosition();
      this._vm.updateVisibleSystems();
    };
    this._vm = viewManager;
    this._scoreViewer = scoreViewer;
    this._init();
  }
  _init() {
    this._settings = new InteractionSettings();
    this._mouseDetails = new _MouseEventDetails();
    this._pointDetails = new PointDetails(null, false, false);
  }
  setNewScore(vScore) {
    this._pah = vScore.pixiAppHelper;
  }
  /**
   * enables scroll interaction - must add to stage before calling this
   */
  enableInteraction() {
    if (!this._enabled) {
      let canvas = this._pah.app.canvas;
      if (this._settings._requiresMouseUpDown) {
        canvas.addEventListener("mousedown", this._onMouseDown);
        window.addEventListener("mouseup", this._onMouseUp);
      }
      if (this._settings._requiresMouseMove) {
        canvas.addEventListener("mousemove", this._onMouseMove);
      }
      if (this._settings._requiresWheel) {
        canvas.addEventListener("wheel", this._onWheel);
      }
      this._pointDetails.matchMeasure = this._settings.enableMeasureClickEvents || this._settings.enableMeasureOverEvents;
      this._pointDetails.matchNoteGroup = this._settings.enableNoteClickEvents || this._settings.enableNoteOverEvents;
      this._pointDetails.matchClickRegions = this._settings.enableRegionClickEvents || this._settings.enableRegionOverEvents;
      this._viewerGlobalPos = this._settings.scoreViewerIsStationary ? this._scoreViewer.toGlobal(new Point(0, 0)) : null;
      this._enabled = true;
    }
  }
  disableInteraction() {
    if (this._enabled) {
      let canvas = this._pah.app.canvas;
      canvas.removeEventListener("mousedown", this._onMouseDown);
      window.removeEventListener("mouseup", this._onMouseUp);
      canvas.removeEventListener("mousemove", this._onMouseMove);
      canvas.removeEventListener("wheel", this._onWheel);
      this._cancelMouseDownWatch();
      this._enabled = false;
    }
  }
  /**Sets the interactions that are available when ScoreViewer playback is not
   * active.*/
  setInteraction(settings) {
    this._settings = settings;
    if (this._enabled) {
      this.disableInteraction();
      this.enableInteraction();
    }
  }
  notifyPlaybackStateChange(isPlaying) {
    if (isPlaying) {
      this.disableInteraction();
    } else {
      this.enableInteraction();
    }
  }
  prepareForRemoval() {
    this.disableInteraction();
  }
  /**Sets extra scrollable space above and below the score */
  setScrollBuffers(aboveScore = 0, belowScore = 100) {
    this._spaceAboveScore = aboveScore;
    this._spaceBelowScore = belowScore;
  }
  _cancelMouseDownWatch() {
    if (this._mouseDownStartPos != null) {
      this._pah.app.ticker.remove(this._onTick);
      this._mouseDownStartPos = null;
      this._mouseDownLastPos = null;
      this._mouseDownPotentialClick = false;
    }
  }
  _fixVerticalScorePosition() {
    let vScore = this._scoreViewer.vScore;
    if (vScore.y + vScore.scoreHeight < this._vm._viewerHeight - this._spaceBelowScore) {
      vScore.y = this._vm._viewerHeight - vScore.scoreHeight - this._spaceBelowScore;
    }
    if (vScore.y > this._spaceAboveScore) {
      vScore.y = this._spaceAboveScore;
    }
  }
  _getMousePositionDetails(e) {
    let rect = this._scoreViewer.vScore.pixiAppHelper.app.canvas.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    let details = this._mouseDetails;
    details.clientMousePos = new Point(e.clientX, e.clientY);
    details.canvasMousePos = new Point(x, y);
    details.viewerMousePos = null;
    details.scoreMousePos = null;
    details.mouseOutsideCanvas = false;
    details.mouseOutsideViewer = false;
    if (x < 0 || x > rect.width || y < 0 || y > rect.height) {
      details.mouseOutsideCanvas = true;
      details.mouseOutsideViewer = true;
      return;
    }
    let viewerGlobalPos = this._viewerGlobalPos || this._scoreViewer.toGlobal(new Point(0, 0));
    let viewerMousePos = new Point(x - viewerGlobalPos.x, y - viewerGlobalPos.y);
    details.viewerMousePos = viewerMousePos;
    if (viewerMousePos.x < 0 || viewerMousePos.x > this._vm._viewerWidth || viewerMousePos.y < 0 || viewerMousePos.y > this._vm._viewerHeight) {
      details.mouseOutsideViewer = true;
      return;
    }
    let scoreGlobalPos = this._scoreViewer.vScore.toGlobal(
      new Point(this._pah.app.stage.x, this._pah.app.stage.y)
    );
    details.scoreMousePos = new Point(x - scoreGlobalPos.x, y - scoreGlobalPos.y);
  }
  _getPointDetailsInScore() {
    this._pointDetails.scorePoint = this._mouseDetails.scoreMousePos;
    this._pointDetails.visualMeasure = null;
    this._pointDetails.visualNoteGroup = null;
    this._pointDetails.visualSystem = null;
    this._pointDetails.clickRegion = null;
    this._scoreViewer.vScore.gridSearch(this._pointDetails);
  }
};
var _MouseEventDetails = class {
  constructor() {
    this.mouseOutsideCanvas = false;
    this.mouseOutsideViewer = false;
  }
};

// src/score_performance/scoreViewer/ScoreViewerPlaybackManager.ts
import { DurationType } from "score_data";
var ScoreViewerPlaybackManager = class {
  constructor(scoreViewer, viewManager, interactionManager, context) {
    this._vngIndex = 0;
    //user controlled setting
    this._forceScrolling = false;
    //num _passedTime = 0;
    this._audioStartDelay = 0.5;
    this._qNoteTime = 0;
    this._isPlaying = false;
    this._onEnterFrame = () => {
      let currentTime = this._getCurrentTime();
      this._qNoteTime = this._getQNoteTime(currentTime);
      this._updateCursorAndScrollPos();
      this._updateCountoffDisplay();
      this._displayRepeatPreview(currentTime);
      let lastVng = this._vngList[this._vngList.length - 1];
      if (this._vngIndex >= this._vngList.length && this._qNoteTime > lastVng.noteGroup.qNoteTime + lastVng.noteGroup.qNoteDuration && this._nextTimeStamp == null) {
        this._stopPlayback();
      }
    };
    this._scoreViewer = scoreViewer;
    this._vm = viewManager;
    this._im = interactionManager;
    this._context = context;
  }
  setNewScore(vScore, vngList) {
    this._vScore = vScore;
    this._vngList = vngList;
    this._cursor = this._vm.drawCursor();
    if (this._isPlaying) {
      this._vScore.addChild(this._cursor);
      this._scoreViewer.pixiAppHelper.requestRender();
    }
  }
  play(timeStamps, beatList, countoffBeatList, audioStartDelaySeconds = 0.5, startNG = null, endQNoteTime = null) {
    if (this._vScore == null) {
      throw "must call setNewScore before using!";
    }
    if (timeStamps == null || timeStamps.length < 1) {
      throw "No time stamps!";
    }
    this._vm.notifyPlaybackStateChange(true);
    this._im.notifyPlaybackStateChange(true);
    this._prepareTracking(
      timeStamps,
      beatList,
      countoffBeatList,
      audioStartDelaySeconds,
      startNG,
      endQNoteTime
    );
    this._prepareScrolling();
    this._vScore.pixiAppHelper.app.ticker.add(this._onEnterFrame);
    this._isPlaying = true;
  }
  stop() {
    this._stopPlayback();
  }
  _prepareTracking(timeStamps, beatList, countoffBeatList, audioStartDelaySeconds, startNG, endQNoteTime) {
    this._contextStartTime = Number.isNaN(this._context.currentTime) ? 0 : this._context.currentTime;
    this._timeStamps = timeStamps;
    this._lastTimeStamp = this._timeStamps[0];
    this._nextTimeStamp = this._timeStamps[1];
    this._currentQNoteToSecondsRatio = this._lastTimeStamp.tempo / 60;
    this._audioStartDelay = audioStartDelaySeconds;
    this._beatList = this._sortAndRemoveDuplicates(beatList.slice(0));
    this._countoffBeatList = countoffBeatList;
    this._beatIndex = 0;
    this._countoffBeatIndex = 0;
    this._firstNoteReached = false;
    this._vngIndex = this._initializeCursorAndView(startNG);
    this._lastReachedVNG = null;
    if (endQNoteTime == null) {
      this._lastVNGInRegion = this._vngList[this._vngList.length - 1];
    } else {
      for (let i = this._vngList.length - 1; i >= 0; i--) {
        if (this._vngList[i].noteGroup.qNoteTime < endQNoteTime) {
          this._lastVNGInRegion = this._vngList[i];
          break;
        }
      }
    }
  }
  _prepareScrolling() {
    this._useScrolling = this._forceScrolling || !this._vm.isNoteInView(this._lastVNGInRegion);
    if (!this._useScrolling) {
      let ts = this._timeStamps[0];
      while (ts.nextSampleTimeStampDO != null) {
        if (ts.nextSampleTimeStampDO.qNoteTime < ts.qNoteEndTime) {
          this._useScrolling = true;
          break;
        }
        ts = ts.nextSampleTimeStampDO;
      }
    }
  }
  _initializeCursorAndView(startNG) {
    let i;
    let vng;
    if (startNG == null) {
      vng = this._vngList[0];
      i = 0;
    } else {
      for (i = 0; i < this._vngList.length; i++) {
        if (this._vngList[i].noteGroup === startNG) {
          vng = this._vngList[i];
          break;
        }
      }
    }
    if (vng == null) {
      vng = this._vngList[0];
      i = 0;
    }
    this._lastSystem = vng.vSystemRef;
    this._vScore.addChild(this._cursor);
    this._vm.scrollToSystem(this._lastSystem, 0);
    this._moveCursorToNotePosition(vng);
    return i;
  }
  _getCurrentTime() {
    let currentTime = this._context.currentTime - this._contextStartTime - this._audioStartDelay;
    return currentTime;
  }
  _getQNoteTime(currentTime) {
    while (this._nextTimeStamp != null && this._nextTimeStamp.msTime / 1e3 <= currentTime) {
      let qNoteTime2 = this._lastTimeStamp.qNoteTime + this._currentQNoteToSecondsRatio * (currentTime - this._lastTimeStamp.msTime / 1e3);
      if (this._nextTimeStamp.qNoteTime < qNoteTime2) {
        this._vngIndex = 0;
        let newStampQNoteTime = this._nextTimeStamp.qNoteTime;
        while (this._vngIndex < this._vngList.length && this._vngList[this._vngIndex].noteGroup.qNoteTime < newStampQNoteTime) {
          this._vngIndex++;
        }
        this._beatIndex = 0;
        while (this._beatIndex < this._beatList.length && this._beatList[this._beatIndex] < newStampQNoteTime) {
          this._beatIndex++;
        }
        this._vm.removeSystemPreview(true);
      }
      this._lastTimeStamp = this._nextTimeStamp;
      this._nextTimeStamp = this._lastTimeStamp.nextSampleTimeStampDO;
      this._currentQNoteToSecondsRatio = this._lastTimeStamp.tempo / 60;
    }
    let qNoteTime = this._lastTimeStamp.qNoteTime + this._currentQNoteToSecondsRatio * (currentTime - this._lastTimeStamp.msTime / 1e3);
    return qNoteTime;
  }
  _updateCursorAndScrollPos() {
    if (!this._firstNoteReached) {
      if (this._qNoteTime < this._beatList[0] && this._qNoteTime < this._vngList[this._vngIndex].noteGroup.qNoteTime) {
        return;
      }
      this._firstNoteReached = true;
    }
    let mostRecentVNG;
    while (this._vngIndex < this._vngList.length && this._vngList[this._vngIndex].noteGroup.qNoteTime <= this._qNoteTime) {
      let vng = this._vngList[this._vngIndex];
      let skipNote = false;
      if (!vng.noteGroup.visible && vng.noteGroup.qNoteTime > vng.noteGroup.voice.measure.stack.startTime) {
        skipNote = true;
      }
      if (!skipNote) {
        mostRecentVNG = vng;
        this._lastReachedVNG = vng;
        let vSystem = mostRecentVNG.vSystemRef;
        if (vSystem !== this._lastSystem) {
          if (this._useScrolling) {
            this._vm.scrollToSystem(vSystem, 0.5);
          }
          this._lastSystem = vSystem;
        }
      }
      let ev = new ScoreViewerEvent(0 /* noteReached */);
      ev.visualNoteGroup = vng;
      ev.qNoteTime = vng.noteGroup.qNoteTime;
      ev.contextTime = this._context.currentTime;
      ev.tempo = this._lastTimeStamp.tempo;
      this._scoreViewer._dispatchClientEvent(ev);
      this._vngIndex++;
    }
    let mostRecentBeat = null;
    while (this._beatIndex < this._beatList.length && this._beatList[this._beatIndex] <= this._qNoteTime) {
      mostRecentBeat = this._beatList[this._beatIndex];
      let ev = new ScoreViewerEvent(1 /* beatReached */);
      ev.qNoteTime = mostRecentBeat;
      ev.contextTime = this._context.currentTime;
      ev.tempo = this._lastTimeStamp.tempo;
      this._scoreViewer._dispatchClientEvent(ev);
      this._beatIndex++;
    }
    if (mostRecentVNG != null && (mostRecentBeat == null || mostRecentVNG.noteGroup.qNoteTime >= mostRecentBeat) && !this._isMeasureWholeRest(mostRecentVNG.noteGroup)) {
      this._moveCursorToNotePosition(mostRecentVNG);
    } else if (mostRecentBeat != null) {
      let prevNG = this._lastReachedVNG?.noteGroup;
      if (prevNG == null) {
        return;
      }
      let nextNG = prevNG.next;
      if (nextNG != null && nextNG.visible === false) {
        nextNG = null;
      }
      let startHPos;
      let distance;
      let endQNoteTime;
      let prevNoteXPos = this._lastReachedVNG.spx;
      let sysPageOffset = 0;
      if (nextNG != null && nextNG.voice.measure === prevNG.voice.measure) {
        startHPos = sysPageOffset + prevNoteXPos;
        distance = nextNG.hPos - prevNG.hPos;
        endQNoteTime = nextNG.qNoteTime;
      } else if (this._isMeasureWholeRest(prevNG)) {
        let stack = prevNG.voice.measure.stack;
        distance = stack.newSystem ? stack.width - stack.indentAsSystemLeader : stack.width - stack.indent;
        startHPos = sysPageOffset + prevNoteXPos - prevNG.hPos;
        startHPos += stack.newSystem ? stack.indentAsSystemLeader : stack.indent;
        endQNoteTime = stack.endTime;
      } else {
        let stack = prevNG.voice.measure.stack;
        startHPos = sysPageOffset + prevNoteXPos;
        distance = stack.width - prevNG.hPos;
        endQNoteTime = stack.endTime;
      }
      let px = this._vScore.scoreProps.pixelsPerSpace;
      this._cursor.x = this._lastSystem.x + px * (startHPos + 0.5 + distance * ((this._qNoteTime - prevNG.qNoteTime) / (endQNoteTime - prevNG.qNoteTime)));
      this._cursor.y = this._lastSystem.scoreY - this._vScore.scoreProps.pixelsPerSpace;
      this._scoreViewer.pixiAppHelper.requestRender();
    }
  }
  _updateCountoffDisplay() {
    if (this._countoffBeatIndex < this._countoffBeatList.length && this._qNoteTime >= this._countoffBeatList[this._countoffBeatIndex]) {
      this._vm.updateVisualCountoff(this._countoffBeatList.length - this._countoffBeatIndex);
      this._countoffBeatIndex++;
    }
  }
  _moveCursorToNotePosition(vng) {
    let px = this._vScore.scoreProps.pixelsPerSpace;
    this._cursor.x = vng.vSystemRef.x + vng.x + 0.5 * this._vScore.scoreProps.noteheadWidth * px;
    this._cursor.y = vng.vSystemRef.scoreY - 1 * this._vScore.scoreProps.pixelsPerSpace;
    this._scoreViewer.pixiAppHelper.requestRender();
  }
  _displayRepeatPreview(currentTime) {
    let lastVng = this._lastSystem.visNoteGroups[this._lastSystem.visNoteGroups.length - 1];
    if (this._nextTimeStamp != null && this._nextTimeStamp.qNoteTime < this._qNoteTime && this._useScrolling && this._vm._systemPreview == null && currentTime + 3 >= this._nextTimeStamp.msTime / 1e3 && this._lastSystem.visNoteGroups[0].noteGroup.qNoteTime > this._nextTimeStamp.qNoteTime && lastVng.noteGroup.qNoteTime + lastVng.noteGroup.qNoteDuration >= this._lastTimeStamp.qNoteEndTime) {
      for (let i = 0; i < this._vngList.length; i++) {
        let vng = this._vngList[i];
        if (vng.noteGroup.qNoteTime >= this._nextTimeStamp.qNoteTime) {
          let vsys = vng.vSystemRef;
          this._vm.showSystemPreview(vsys, this._lastSystem, vng);
          break;
        }
      }
    }
  }
  _stopPlayback() {
    if (this._isPlaying) {
      this._vScore.pixiAppHelper.app.ticker.remove(this._onEnterFrame);
      this._isPlaying = false;
      this._vm.notifyPlaybackStateChange(false);
      this._im.notifyPlaybackStateChange(false);
      this._cursor.parent.removeChild(this._cursor);
      this._scoreViewer.pixiAppHelper.requestRender();
    }
  }
  _isMeasureWholeRest(ng) {
    return ng.isRest && ng.durationType === DurationType.WHOLE && ng.voice.noteGroups.length === 1;
  }
  _sortAndRemoveDuplicates(list) {
    list.sort((num1, num2) => {
      return num1 <= num2 ? -1 : 1;
    });
    let newList = [];
    let lastNum;
    for (let i = 0; i < list.length; i++) {
      let newNum = list[i];
      if (newNum !== lastNum) {
        newList.push(newNum);
      }
      lastNum = newNum;
    }
    return newList;
  }
  get qNoteTime() {
    return this._qNoteTime;
  }
  get tempo() {
    return this._lastTimeStamp?.tempo;
  }
  /**Setting this to true will force system scrolling when the cursor
   * reaches a new line. If this is false, scrolling will still occur if
   * notes are not visible on the screen.*/
  get forceScrolling() {
    return this._forceScrolling;
  }
  set forceScrolling(value) {
    this._forceScrolling = value;
  }
};
var TimeTrackMode = /* @__PURE__ */ ((TimeTrackMode2) => {
  TimeTrackMode2[TimeTrackMode2["AUDIO_CONTEXT"] = 0] = "AUDIO_CONTEXT";
  TimeTrackMode2[TimeTrackMode2["BEAT_HITS"] = 1] = "BEAT_HITS";
  return TimeTrackMode2;
})(TimeTrackMode || {});

// src/score_performance/scoreViewer/ScoreViewerViewManager.ts
import { VisualPage } from "score_render";
import { Text, Graphics, Container } from "pixi.js";
import { Easing, Tween } from "@tweenjs/tween.js";
import { createEmitter as createEmitter2 } from "ts_utils";
var _ScoreViewerViewManager = class _ScoreViewerViewManager {
  constructor(scoreViewer) {
    this._viewerWidth = 500;
    this._viewerHeight = 400;
    this._systemScrollEmitter = createEmitter2();
    this._onUpdateTweens = () => {
      let runAgain = false;
      if (this._scrollTween != null) {
        runAgain = true;
        this._scrollTween.update();
      }
      if (this._countoffFadeTween != null) {
        runAgain = true;
        this._countoffFadeTween.update();
      }
      if (this._systemPreviewScrollTween != null) {
        runAgain = true;
        this._systemPreviewScrollTween.update();
      }
      if (runAgain) {
        this._scoreViewer.pixiAppHelper.requestRender();
        requestAnimationFrame(this._onUpdateTweens);
      }
    };
    this._scoreViewer = scoreViewer;
    this._init();
  }
  get systemScrollEvent() {
    return this._systemScrollEmitter.event;
  }
  _init() {
    this._scoreViewer._assessmentOverlay = new Container();
  }
  setNewScore(visualScore, viewerWidth = -1, viewerHeight = -1) {
    if (this._vScore != null && this._vScore.parent === this._scoreViewer) {
      this._scoreViewer.removeChild(this._vScore);
    }
    this._vScore = visualScore;
    this._vSystems = this._vScore.getVisualSystems();
    if (viewerWidth === -1)
      viewerWidth = this._viewerWidth;
    if (viewerHeight === -1)
      viewerHeight = this._viewerHeight;
    this.setWidthAndHeight(viewerWidth, viewerHeight);
    this._scoreViewer.addChild(this._vScore);
    this._scoreViewer._assessmentOverlay.parent?.removeChild(
      this._scoreViewer._assessmentOverlay
    );
    this._vScore.addChild(this._scoreViewer._assessmentOverlay);
  }
  setWidthAndHeight(viewerWidth, viewerHeight) {
    this._viewerWidth = viewerWidth;
    this._viewerHeight = viewerHeight;
    if (this._viewerMask && this._viewerMask.parent === this._scoreViewer) {
      this._scoreViewer.removeChild(this._viewerMask);
    }
    let mask = new Graphics();
    let e = _ScoreViewerViewManager.EDGE_BUFFER;
    mask.rect(-e, 0, this._viewerWidth + 2 * e, this._viewerHeight);
    mask.fill(0);
    this._scoreViewer.mask = mask;
    this._scoreViewer.addChild(mask);
    this._viewerMask = mask;
    this._scoreViewer.pixiAppHelper.requestRender();
    if (this._viewerWidth != null && this._vScore != null) {
      this._vScore.x = 0;
      this.updateVisibleSystems(0, true);
    }
  }
  notifyPlaybackStateChange(isPlaying) {
    if (!isPlaying) {
      this.removeSystemPreview(false);
    }
  }
  prepareForRemoval() {
    this.stopCurrentScrollTween();
    this.removeSystemPreview(false);
    if (this._vScore != null && this._vScore.parent === this._scoreViewer) {
      this._scoreViewer.removeChild(this._vScore);
      this._vScore = null;
    }
    this._scoreViewer.pixiAppHelper.requestRender();
  }
  isNoteInView(vng) {
    let vSys = vng.vSystemRef;
    if (vSys.parent == null) {
      return false;
    } else {
      if (this._vScore.y + vSys.scoreY > 0 && this._vScore.y + (vSys.scoreY + vSys.height) < this._viewerHeight) {
        return true;
      }
      return false;
    }
  }
  /**returns the first [VisualSystem] object that is currently in view. If
   * none are found in view, null is returned.*/
  getFirstVisibleSystem() {
    for (let i = 0; i < this._vSystems.length; i++) {
      let vSys = this._vSystems[i];
      if (this._vScore.y + (vSys.scoreY + vSys.height) > 0 && this._vScore.y + vSys.scoreY < this._viewerHeight) {
        return vSys;
      }
    }
    return null;
  }
  /**returns the [VisualSystem] objects that are currently in view. Returns an
   * empty array if none are in view. */
  getVisibleSystems() {
    return this._vSystems.filter((vsys) => {
      return this._vScore.y + (vsys.scoreY + vsys.height) > 0 && this._vScore.y + vsys.scoreY < this._viewerHeight;
    });
  }
  scrollToSystem(vSystem, seconds) {
    if (this._scrollTween != null) {
      this._scrollTween.end();
      this._scrollTween = null;
    }
    let targetY = this._vScore.firstPageVerticalOffset + -1 * vSystem.scoreY;
    if (seconds > 0 && this._vScore.y > targetY) {
      this.updateVisibleSystems(this._vScore.y - targetY);
      this._scrollTween = new Tween(this._vScore).to({ y: targetY }, 500).easing(Easing.Quadratic.Out).onComplete(() => {
        this._scrollTween = null;
        this._systemScrollEmitter.emit();
      }).start();
      this._onUpdateTweens();
    } else {
      this._vScore.y = targetY;
      this.updateVisibleSystems(0);
    }
  }
  stopCurrentScrollTween() {
    if (this._scrollTween != null) {
      this._scrollTween.end();
      this._scrollTween = null;
    }
  }
  /**updates which systems are currently in view. This caches the visible 
   * systems and removes the cache for systems which are out of view (as well 
   * as removing them from the stage). Use cacheAlreadyVisibleSystems the 
   * first time a score is processed to make sure that previously visible 
   * systems are cached.*/
  updateVisibleSystems(nextScrollDelta = 0, cacheAlreadyVisibleSystems = false) {
    for (let i = 0; i < this._vSystems.length; i++) {
      let vSys = this._vSystems[i];
      if (this._vScore.y + (vSys.scoreY + vSys.height) > 0 && this._vScore.y + vSys.scoreY < this._viewerHeight + nextScrollDelta) {
        if (vSys.parent !== vSys.pageRef || cacheAlreadyVisibleSystems) {
          vSys.displayTexture();
        }
        if (vSys.parent !== vSys.pageRef) {
          vSys.pageRef.addChild(vSys);
        }
      } else {
        if (vSys.parent === vSys.pageRef) {
          vSys.pageRef.removeChild(vSys);
        }
      }
    }
    this._scoreViewer.pixiAppHelper.requestRender();
  }
  updateVisualCountoff(countoffBeat) {
    if (this._countoffFadeTween != null && this._countoffFadeTween.isPlaying) {
      this._countoffFadeTween.end();
    }
    if (this._countoffDisplay != null && this._countoffDisplay.parent === this._scoreViewer) {
      this._scoreViewer.removeChild(this._countoffDisplay);
    }
    this._countoffDisplay = new Container();
    let text;
    if (countoffBeat === 2) {
      text = "Set";
    } else if (countoffBeat === 1) {
      text = "Go";
    } else {
      text = countoffBeat.toString();
    }
    let tf = new Text({ text, style: {
      fontFamily: "Arial",
      fill: 65280,
      fontSize: 80 * this._viewerWidth / 600,
      align: "left"
    } });
    this._countoffDisplay.addChild(tf);
    this._countoffDisplay.x = (this._viewerWidth - tf.width) / 2;
    this._countoffDisplay.y = (this._viewerHeight - tf.height) / 2;
    this._scoreViewer.addChild(this._countoffDisplay);
    this._countoffFadeTween = new Tween(this._countoffDisplay).to({ alpha: 0 }).easing(Easing.Linear.None).onComplete(() => {
      if (this._countoffDisplay.parent === this._scoreViewer) {
        this._scoreViewer.removeChild(this._countoffDisplay);
      }
      this._countoffFadeTween = null;
    }).start();
    this._onUpdateTweens();
  }
  drawCursor(color = 65280) {
    let px = this._vScore.scoreProps.pixelsPerSpace;
    let graphics = new Graphics();
    graphics.setStrokeStyle({
      color,
      alpha: 0.3,
      width: 20 * px / 10,
      join: "round",
      cap: "square"
    });
    graphics.moveTo(0, 0);
    graphics.lineTo(0, px * 6);
    graphics.y = -1 * px;
    graphics.stroke();
    return graphics;
  }
  showSystemPreview(targetSys, currentSys, targetVng) {
    this.removeSystemPreview(false);
    let preview = new Container();
    let g = new Graphics();
    g.rect(targetSys.x, targetSys.y, targetSys.width, targetSys.height);
    g.fill(16777215);
    preview.addChild(g);
    preview.addChild(targetSys.getTextureSprite());
    this._systemPreview = preview;
    let cursor = this.drawCursor();
    this._systemPreview.x = this._vScore.x;
    this._systemPreview.y = this._vScore.firstPageVerticalOffset + currentSys.height + VisualPage.DISTANCE_BETWEEN_SYSTEMS;
    this._scoreViewer.addChild(this._systemPreview);
    let r = currentSys.graphicsBounds;
    let graphics = new Graphics();
    graphics.rect(r.x, r.y, r.width, r.height);
    graphics.fill(0);
    graphics.x = currentSys.scoreX + r.x;
    graphics.y = currentSys.scoreY;
    this._vScore.addChild(graphics);
    this._vScore.mask = graphics;
    this._scoreViewer.pixiAppHelper.requestRender();
  }
  removeSystemPreview(fadeIntoPosition) {
    if (this._systemPreview != null && this._systemPreview.parent != null) {
      if (fadeIntoPosition) {
        let tempSys = this._systemPreview;
        this._systemPreviewScrollTween = new Tween(tempSys).to({ y: this._vScore.firstPageVerticalOffset }, 500).easing(Easing.Cubic.Out).onComplete(() => {
          tempSys.parent.removeChild(tempSys);
          this._vScore.mask = null;
          this._systemPreviewScrollTween = null;
        }).start();
        this._onUpdateTweens();
      } else {
        this._systemPreviewScrollTween = null;
        this._systemPreview.parent.removeChild(
          this._systemPreview
        );
        this._vScore.removeChild(this._vScore.mask);
        this._vScore.mask = null;
      }
      this._scoreViewer.pixiAppHelper.requestRender();
    }
    if (this._systemPreview != null) {
      this._systemPreview = null;
      this._vScore.removeChild(this._vScore.mask);
      this._vScore.mask = null;
      this._scoreViewer.pixiAppHelper.requestRender();
    }
  }
};
/**gap between scoreViewer edge and visualScore*/
_ScoreViewerViewManager.EDGE_BUFFER = 2;
var ScoreViewerViewManager = _ScoreViewerViewManager;

// src/score_performance/scoreViewer/ScoreViewer.ts
var ScoreViewer3 = class extends Container2 {
  constructor(pixiAppHelper, visualScore, viewerWidth, viewerHeight, context) {
    super();
    this._emitter = createEmitter3();
    this._pah = pixiAppHelper;
    this._init(visualScore, viewerWidth, viewerHeight, context);
  }
  get event() {
    return this._emitter.event;
  }
  _init(visualScore, viewerWidth, viewerHeight, context) {
    this._vm = new ScoreViewerViewManager(this);
    this._im = new ScoreViewerInteractionManager(this._vm, this);
    this._pm = new ScoreViewerPlaybackManager(
      this,
      this._vm,
      this._im,
      context
    );
    this.setScore(visualScore, viewerWidth, viewerHeight);
    this._im.enableInteraction();
  }
  setScore(visualScore, viewerWidth = -1, viewerHeight = -1) {
    this._vScore = visualScore;
    this._vngList = this._vScore.getVisualNoteGroups();
    this._vngList.sort((vng1, vng2) => vng1.noteGroup.qNoteTime <= vng2.noteGroup.qNoteTime ? -1 : 1);
    this._vm.setNewScore(this._vScore, viewerWidth, viewerHeight);
    this._pm.setNewScore(this._vScore, this._vngList);
    this._im.setNewScore(this._vScore);
  }
  prepareForRemoval() {
    this._pm.stop();
    this._vm.prepareForRemoval();
    this._im.prepareForRemoval();
    this._vScore = null;
  }
  setWidthAndHeight(viewerWidth, viewerHeight) {
    this._vm.setWidthAndHeight(viewerWidth, viewerHeight);
  }
  play(timeStamps, beatList, countoffBeatList, audioStartDelaySeconds = 0.5, startNG = null, endQNoteTime = null) {
    this._pm.play(
      timeStamps,
      beatList,
      countoffBeatList,
      audioStartDelaySeconds,
      startNG,
      endQNoteTime
    );
  }
  stop() {
    this._pm.stop();
  }
  startBeatTrackPlayback(countoffBeats) {
  }
  /**
   * scrolls the score to the indicated system
   */
  scrollToSystem(system, seconds) {
    for (let vSystem of this._vm._vSystems) {
      if (vSystem.systemRef === system) {
        this._vm.scrollToSystem(vSystem, seconds);
        return;
      }
    }
  }
  /**scrolls the score to the top system*/
  scrollToTop(seconds) {
    this.scrollToSystem(this._vm._vSystems[0].systemRef, seconds);
  }
  /**returns the first [VisualSystem] object that is currently in view. If
   * none are found in view, null is returned.*/
  getFirstVisibleSystem() {
    return this._vm.getFirstVisibleSystem();
  }
  /**returns the [VisualSystem] objects that are currently in view. Returns an
   * empty array if none are in view. */
  getVisibleSystems() {
    return this._vm.getVisibleSystems();
  }
  /**Sets the interactions that are available when ScoreViewer playback is not
   * active.*/
  setInteraction(settings) {
    this._im.setInteraction(settings);
  }
  draw1pxFrame(color = 16711680) {
    if (this._frame != null && this._frame.parent === this) {
      this.removeChild(this._frame);
    }
    this._frame = new Graphics2();
    this._frame.setStrokeStyle({ color, width: 1 });
    this._frame.rect(0, 0, this.width, this.height);
    this._frame.stroke();
    this.addChildAt(this._frame, 0);
  }
  /**Sets extra scrollable space above and below the score */
  setScrollBuffers(aboveScore = 0, belowScore = 100) {
    this._im.setScrollBuffers(aboveScore, belowScore);
  }
  _dispatchClientEvent(e) {
    this._emitter.emit(e);
  }
  get pixiAppHelper() {
    return this._pah;
  }
  get vScore() {
    return this._vScore;
  }
  /**returns a list of the VisualNoteGroups, sorted by qNoteTime*/
  get vngList() {
    return this._vngList;
  }
  get viewManager() {
    return this._vm;
  }
  get playbackManager() {
    return this._pm;
  }
  get assessmentOverlay() {
    return this._assessmentOverlay;
  }
  /**Setting this to true will force system scrolling when the cursor
   * reaches a new line. If this is false, scrolling will still occur if
   * notes are not visible on the screen.*/
  get forceScrolling() {
    return this._pm.forceScrolling;
  }
  set forceScrolling(value) {
    this._pm.forceScrolling = value;
  }
};
var ScoreViewerEvent = class {
  constructor(type) {
    this.type = type;
  }
};
var ScoreViewerEventType = /* @__PURE__ */ ((ScoreViewerEventType2) => {
  ScoreViewerEventType2[ScoreViewerEventType2["noteReached"] = 0] = "noteReached";
  ScoreViewerEventType2[ScoreViewerEventType2["beatReached"] = 1] = "beatReached";
  ScoreViewerEventType2[ScoreViewerEventType2["measureClicked"] = 2] = "measureClicked";
  ScoreViewerEventType2[ScoreViewerEventType2["measureMouseOver"] = 3] = "measureMouseOver";
  ScoreViewerEventType2[ScoreViewerEventType2["noteGroupClicked"] = 4] = "noteGroupClicked";
  ScoreViewerEventType2[ScoreViewerEventType2["noteGroupMouseOver"] = 5] = "noteGroupMouseOver";
  ScoreViewerEventType2[ScoreViewerEventType2["regionClicked"] = 6] = "regionClicked";
  ScoreViewerEventType2[ScoreViewerEventType2["regionOver"] = 7] = "regionOver";
  return ScoreViewerEventType2;
})(ScoreViewerEventType || {});

// src/score_performance/assessment/NoteSplitter.ts
var NoteSplitter = class {
  constructor() {
    this._prEvents = [];
    this._pitchHistory = [];
    this._currentEventIndex = 0;
    /**/
    ///SHARED SPLIT PARAMS//////////*/
    /**/
    ///OPTIONAL SPLIT PARAMS////////*/
    this._osMinStablePitches = 6;
    this._osPitchesToConsider = 9;
    this._osMinTotalPitchJump = 60;
    this._osMinPitchStabilityThreshold = 40;
    this._osLastStablePitch = 0;
    //keeps track of the number of events that have kept stable and are added
    //into our average
    this._osStablePitchEventsCount = 0;
    this._osMinAmplPercRise = 1;
    this._osAmplMaxRiseTime = 0.1;
    this._osLastSplitTime = 0;
    this._minTimeBetweenSplits = 0.1;
    //minimum amplitude a pitch event must be to be considered as a split point
    this._minAmplPerc = 0;
    this._highestAmpl = 0;
  }
  //sets note splitter parameters using settings from the passed in instrument
  setInstrument(inst) {
    if (inst.psMinStablePitches != null) {
      this._osMinStablePitches = inst.psMinStablePitches;
    }
    if (inst.psPitchesToConsider != null) {
      this._osPitchesToConsider = inst.psPitchesToConsider;
    }
    if (inst.psMinTotalPitchJump != null) {
      this._osMinTotalPitchJump = inst.psMinTotalPitchJump;
    }
    if (inst.psMinPitchStabilityThresh != null) {
      this._osMinPitchStabilityThreshold = inst.psMinPitchStabilityThresh;
    }
    if (inst.asMinAmplPercRise != null) {
      this._osMinAmplPercRise = inst.asMinAmplPercRise;
    }
    if (inst.asMaxAmplRiseTime != null) {
      this._osAmplMaxRiseTime = inst.asMaxAmplRiseTime;
    }
    if (inst.spMinTimeBetweenSplits != null) {
      this._minTimeBetweenSplits = inst.spMinTimeBetweenSplits;
    }
    if (inst.minAmplPerc != null) {
      this._minAmplPerc = inst.minAmplPerc;
    }
  }
  /**
   * resets the splitter for a new performance
   */
  reset() {
    this._prEvents = [];
    this._pitchHistory = [];
    this._currentEventIndex = 0;
    this._osLastStablePitch = 0;
    this._osStablePitchEventsCount = 0;
    this._osLastSplitTime = 0;
    this._highestAmpl = 0;
  }
  addEvent(prEvent) {
    this._prEvents.push(prEvent);
    this._pitchHistory.push(prEvent.cents);
    let cprEvent = this._prEvents[this._currentEventIndex];
    if (cprEvent.rms > this._highestAmpl) {
      this._highestAmpl = cprEvent.rms;
    } else if (cprEvent.rms < this._minAmplPerc * this._highestAmpl) {
      this._currentEventIndex++;
      return;
    }
    let cPitch = prEvent.cents;
    if ((cPitch - this._osLastStablePitch >= this._osMinTotalPitchJump || this._osLastStablePitch - cPitch >= this._osMinTotalPitchJump) && this._currentEventIndex - this._osPitchesToConsider + 1 >= 0) {
      let firstPitchIndex = this._currentEventIndex - this._osPitchesToConsider + 1;
      let firstSamePitchIndex = -1;
      let stableCount = 1;
      let stableTotal = cPitch;
      let pitchStable = false;
      for (let i = firstPitchIndex; i < this._currentEventIndex; i++) {
        if (this._pitchHistory[i] - cPitch < this._osMinPitchStabilityThreshold && cPitch - this._pitchHistory[i] < this._osMinPitchStabilityThreshold) {
          stableCount++;
          stableTotal += this._pitchHistory[i];
          if (firstSamePitchIndex === -1) {
            firstSamePitchIndex = i;
          }
          if (stableCount >= this._osMinStablePitches) {
            pitchStable = true;
          }
        }
      }
      if (pitchStable) {
        let newAverage = ~~(stableTotal / stableCount);
        if (newAverage - this._osLastStablePitch >= this._osMinTotalPitchJump || this._osLastStablePitch - newAverage >= this._osMinTotalPitchJump) {
          this._osLastStablePitch = newAverage;
          this._osStablePitchEventsCount = stableCount;
          this._osLastSplitTime = this._prEvents[firstPitchIndex].time;
          this._prEvents[firstSamePitchIndex].pitchSplit = true;
          this._prEvents[firstSamePitchIndex].splitCents = this._osLastStablePitch;
        }
      }
    } else if (cPitch - this._osLastStablePitch < this._osMinPitchStabilityThreshold && this._osLastStablePitch - cPitch < this._osMinPitchStabilityThreshold) {
      this._osStablePitchEventsCount++;
      if (this._osStablePitchEventsCount < 2) {
        this._osStablePitchEventsCount = 2;
      }
      this._osLastStablePitch = ~~(((this._osStablePitchEventsCount - 1) * this._osLastStablePitch + cPitch) / this._osStablePitchEventsCount);
    }
    if (cprEvent.time - this._minTimeBetweenSplits <= this._osLastSplitTime) {
      this._currentEventIndex++;
      return;
    }
    let prevEventIndex = this._currentEventIndex - 1;
    while (prevEventIndex >= 0 && this._prEvents[prevEventIndex].time >= cprEvent.time - this._osAmplMaxRiseTime) {
      if (this._prEvents[prevEventIndex].rms * (1 + this._osMinAmplPercRise) <= cprEvent.rms) {
        cprEvent.amplSplit = true;
        this._osLastSplitTime = cprEvent.time;
        break;
      }
      prevEventIndex--;
    }
    this._currentEventIndex++;
  }
};

// src/score_performance/assessment/PitchAssessor.ts
var PitchAssessor = class extends Assessor {
  constructor(scoreViewer, visualizer) {
    super(scoreViewer, visualizer);
    //number of milliseconds to wait before checking assessment for a note
    this._assess_Delay = 0.8;
    this._debugPRecString = "";
    this._debugNotesString = "";
    this._debug = false;
    this._onScoreViewerEvent = (e) => {
      if (e.type === 0 /* noteReached */) {
        this._notationNotesQue.push(e);
        if (this._debug)
          this._debugNREvents.push(e);
      }
    };
    this._onPitchRec = (e) => {
      this._prEvents.push(e);
      this._pitchHistory.push(e.cents);
      if (this._inst.checkNoteStartTimes) {
        this._noteSplitter.addEvent(e);
      }
      if (this._debug) {
        this._debugPREvents.push(e);
      }
    };
    this._onEnterFrame = () => {
      this._checkForNotesToAssess(false);
    };
    this._initialize();
  }
  _initialize() {
    this._slm = SoundLibManager2.get();
    if (!MediaDeviceManager.isInitialized || !MediaDeviceManager.get().isMicReady) {
      throw "You must initialize the MediaDeviceManager and get mic permission before using microphone pitch assessment";
    }
    this._pitchRecognizer = new PitchRecognizer(MicManager.get());
    this._noteSplitter = new NoteSplitter();
  }
  setInstrument(inst) {
    this._inst = AssessmentInstrumentUtils.getInstrumentByID(inst.id);
    if (this._inst == null)
      throw "InstrumentDO.id doesn't match an assessment inst";
    this._pitchRecognizer.setInstrument(this._inst);
    this._noteSplitter.setInstrument(this._inst);
  }
  /**starts assessment*/
  start() {
    if (this._debug) {
      this._debugNREvents = [];
      this._debugPREvents = [];
      this._debugMatchedNotes = "";
    }
    this._hitMissHistory = [];
    this._prEvents = [];
    this._pitchHistory = [];
    this._prEventWindowStartIndex = 0;
    this._lastNotationPitch = 0;
    this._maxAmplitudeEncountered = 0;
    this._noteSplitter.reset();
    this._notationNotesQue = [];
    this._scoreViewer.event.on(this._onScoreViewerEvent);
    this._scoreViewer.vScore.pixiAppHelper.app.ticker.add(
      this._onEnterFrame
    );
    this._pitchRecognizer.setInstrument(this._inst);
    this._pitchRecognizer.pitchRecEvent.on(this._onPitchRec);
    this._pitchRecognizer.startPRec(false);
    this._debugNotesString = "tuning window: ${this._inst.tuningWindow}\n";
    this._debugPRecString = "";
  }
  /**stops assessment*/
  stop() {
    if (this._pitchRecognizer.isRunning) {
      this._pitchRecognizer.stopPRec(false);
      this._scoreViewer.vScore.pixiAppHelper.app.ticker.remove(
        this._onEnterFrame
      );
      this._checkForNotesToAssess(true);
      this._pitchRecognizer.pitchRecEvent.off(this._onPitchRec);
      this._scoreViewer.event.off(this._onScoreViewerEvent);
      if (this._debug) {
        console.log(this._debugNotesString);
        console.log(this._debugPRecString);
      }
    }
  }
  get requiresMicrophone() {
    return true;
  }
  _checkForNotesToAssess(isFinalFrame) {
    let cTime = this._slm.context.currentTime;
    while (this._notationNotesQue.length > 0 && (cTime - this._assess_Delay >= this._notationNotesQue[0].contextTime || isFinalFrame)) {
      let nrEvent = this._notationNotesQue.splice(0, 1)[0];
      let ng = nrEvent.visualNoteGroup.noteGroup;
      if (!ng.isRest && !ng.notes[0].isTieTarget) {
        if (!this._inst.checkNoteStartTimes) {
          this.assessNote(nrEvent, cTime);
        } else {
          this.assessNote4(nrEvent, cTime);
        }
      } else {
        this._lastNotationPitch = 0;
      }
    }
  }
  assessNote(noteReachedEvent, cTime) {
    let noteStartTime = noteReachedEvent.contextTime;
    let assessNote = this.getAssessNoteFromVNG(
      noteReachedEvent.visualNoteGroup
    );
    let noteGroup = assessNote.vng.noteGroup;
    let note = noteGroup.notes[0];
    let durationInSeconds = noteGroup.qNoteDuration * (60 / noteReachedEvent.tempo);
    let requiredMatches = Math.ceil(durationInSeconds * this._inst.requiredPitchesPerSecond);
    let matchedPitches = 0;
    let notationCents = note.displayCents - this._inst.transposition * 100;
    let timeWindow = this._inst.timeBucketRight * Math.pow(durationInSeconds, 0.3);
    let allowMultipleGuesses = this._pitchRecognizer.pitchRecMethod === 4;
    let maxGuesses = 3;
    let length = this._prEvents.length;
    for (let i = 0; i < length; i++) {
      let pitchRecEvent = this._prEvents[i];
      pitchRecEvent.correlationScore = -5;
      if (pitchRecEvent.time >= noteStartTime - timeWindow && pitchRecEvent.time <= noteStartTime + timeWindow) {
        let pitchMatch = false;
        if (!allowMultipleGuesses) {
          let pRecCents = pitchRecEvent.cents;
          if (!this._inst.ignoreOctave) {
            pitchMatch = notationCents - pRecCents < this._inst.tuningWindow && pRecCents - notationCents < this._inst.tuningWindow;
          } else {
            let difference = notationCents >= pRecCents ? (notationCents - pRecCents) % 1200 : (pRecCents - notationCents) % 1200;
            pitchMatch = difference < this._inst.tuningWindow || difference > 1200 - this._inst.tuningWindow;
          }
        } else {
          let guesses = pitchRecEvent.centsGuessList;
          let numGuesses = guesses.length;
          if (numGuesses > maxGuesses) {
            numGuesses = maxGuesses;
          }
          for (let j = 0; j < numGuesses; j++) {
            let pRecCents = guesses[j];
            if (this._inst.ignoreOctave) {
              if (notationCents - pRecCents < this._inst.tuningWindow && pRecCents - notationCents < this._inst.tuningWindow) {
                pitchMatch = true;
                break;
              }
            } else {
              let difference = notationCents >= pRecCents ? (notationCents - pRecCents) % 1200 : (pRecCents - notationCents) % 1200;
              if (difference < this._inst.tuningWindow || difference > 1200 - this._inst.tuningWindow) {
                pitchMatch = true;
                break;
              }
            }
          }
        }
        if (pitchMatch) {
          matchedPitches++;
          if (matchedPitches >= requiredMatches) {
            this._markNoteCorrect(assessNote);
            this._hitMissHistory.push(1);
            if (i + 1 >= matchedPitches && this._debug) {
              this._debugMatchedNotes += `matched note: precTime: ${this._prEvents[i + 1 - matchedPitches].time} noteTime: $noteStartTime
`;
              console.log(`matched note: precTime: ${this._prEvents[i + 1 - matchedPitches].time}noteTime: ${noteStartTime}`);
            }
            return;
          }
        }
      } else if (pitchRecEvent.time < noteStartTime - timeWindow) {
        this._prEvents.splice(i, 1);
        i--;
        length--;
      } else if (pitchRecEvent.time > noteStartTime + timeWindow) {
        break;
      }
    }
    this._markNoteIncorrect(assessNote);
    this._hitMissHistory.push(0);
  }
  assessNote4(noteReachedEvent, cTime) {
    let noteStartTime = noteReachedEvent.contextTime;
    let assessNote = this.getAssessNoteFromVNG(
      noteReachedEvent.visualNoteGroup
    );
    let noteGroup = assessNote.vng.noteGroup;
    let note = noteGroup.notes[0];
    let durationInSeconds = noteGroup.qNoteDuration * (60 / noteReachedEvent.tempo);
    let requiredMatches = durationInSeconds < 1 ? Math.ceil(durationInSeconds * this._inst.requiredPitchesPerSecond) : this._inst.requiredPitchesPerSecond;
    let matchedPitches = 0;
    let notationCents = note.displayCents - this._inst.transposition * 100;
    let twLeft;
    let twRight;
    if (durationInSeconds >= 1) {
      twLeft = this._inst.timeBucketLeft;
      twRight = this._inst.timeBucketRight;
    } else {
      twLeft = this._inst.timeBucketLeft * Math.pow(durationInSeconds, 0.4);
      twRight = this._inst.timeBucketRight * Math.pow(durationInSeconds, 0.4);
    }
    let allowMultipleGuesses = this._pitchRecognizer.pitchRecMethod === 4;
    let maxGuesses = 3;
    let haveSplitPointInTimeWindow = false;
    if (this._debug) {
      this._debugNotesString += `${note.pitchName} cents: $notationCents${noteStartTime} r ${requiredMatches} w ${twLeft} / ${twRight} ct ${cTime}
`;
    }
    let length = this._prEvents.length;
    for (let i = this._prEventWindowStartIndex; i < length; i++) {
      let pitchRecEvent = this._prEvents[i];
      let logThisEvent = true;
      let prEventTime = pitchRecEvent.time;
      let pRecCents = pitchRecEvent.cents;
      if (logThisEvent && this._debug) {
        this._debugPRecString += "\n${pitchRecEvent.pitchName} ${pitchRecEvent.time} cents: ${pitchRecEvent.cents} ct $cTime";
      }
      let prEventInTimeWindow = prEventTime >= noteStartTime - twLeft && prEventTime <= noteStartTime + twRight;
      if (pitchRecEvent.pitchSplit || pitchRecEvent.amplSplit) {
        haveSplitPointInTimeWindow = prEventInTimeWindow;
      }
      let rms = pitchRecEvent.rms;
      if (rms > this._maxAmplitudeEncountered) {
        this._maxAmplitudeEncountered = rms;
      }
      if (rms < this._maxAmplitudeEncountered * this._inst.minAmplPerc) {
      } else if (haveSplitPointInTimeWindow && prEventTime <= noteStartTime + 2 * twRight || prEventTime >= noteStartTime && prEventTime <= noteStartTime + twRight && this._lastNotationPitch === notationCents) {
        if (logThisEvent && this._debug) {
          this._debugPRecString += "  tm!";
        }
        let pitchMatch = false;
        if (!allowMultipleGuesses) {
          if (!this._inst.ignoreOctave) {
            pitchMatch = notationCents - pRecCents < this._inst.tuningWindow && pRecCents - notationCents < this._inst.tuningWindow;
          } else {
            let difference = notationCents >= pRecCents ? (notationCents - pRecCents) % 1200 : (pRecCents - notationCents) % 1200;
            pitchMatch = difference < this._inst.tuningWindow || difference > 1200 - this._inst.tuningWindow;
          }
        } else {
          let guesses = pitchRecEvent.centsGuessList;
          let numGuesses = guesses.length;
          if (numGuesses > maxGuesses) {
            numGuesses = maxGuesses;
          }
          for (let j = 0; j < numGuesses; j++) {
            pRecCents = guesses[j];
            if (!this._inst.ignoreOctave) {
              if (notationCents - pRecCents < this._inst.tuningWindow && pRecCents - notationCents < this._inst.tuningWindow) {
                pitchMatch = true;
                break;
              }
            } else {
              let difference = notationCents >= pRecCents ? (notationCents - pRecCents) % 1200 : (pRecCents - notationCents) % 1200;
              if (difference < this._inst.tuningWindow || difference > 1200 - this._inst.tuningWindow) {
                pitchMatch = true;
                break;
              }
            }
          }
        }
        if (pitchMatch) {
          matchedPitches++;
          if (logThisEvent && this._debug) {
            this._debugPRecString += "  m+ $matchedPitches";
          }
          if (matchedPitches >= requiredMatches) {
            this._lastNotationPitch = notationCents;
            this._markNoteCorrect(assessNote);
            this._hitMissHistory.push(1);
            if (i + 1 >= matchedPitches && this._debug) {
              this._debugMatchedNotes += `matched note: precTime: ${this._prEvents[i + 1 - matchedPitches].time}noteTime: ${noteStartTime}
`;
              console.log(`matched note: precTime:${this._prEvents[i + 1 - matchedPitches].time}noteTime: ${noteStartTime}`);
            }
            return;
          }
        }
      }
      if (prEventTime < noteStartTime - twLeft) {
        this._prEventWindowStartIndex++;
      } else if (prEventTime > noteStartTime + twRight && !haveSplitPointInTimeWindow) {
        break;
      }
    }
    this._lastNotationPitch = notationCents;
    this._markNoteIncorrect(assessNote);
    this._hitMissHistory.push(0);
  }
  //DEBUG
  get debugPRecString() {
    return this._debugPRecString;
  }
  get debugNotesString() {
    return this._debugNotesString;
  }
  get debugPREvents() {
    return this._debugPREvents;
  }
  /**List of NoteReached events from ScoreViewer.*/
  get debugNREvents() {
    return this._debugNREvents;
  }
  get debugMatchedNotes() {
    return this._debugMatchedNotes;
  }
  /**
   * if true, data will be sent to debug output
   */
  get debug() {
    return this._debug;
  }
  set debug(value) {
    this._debug = value;
  }
};

// src/score_performance/assessment/RhythmAssessor.ts
import {
  AssessmentInstrumentUtils as AssessmentInstrumentUtils2,
  HitType as HitType2,
  KeyHitRecognizer as KeyHitRecognizer2,
  MediaDeviceManager as MediaDeviceManager2,
  MicHitRecognizer,
  MicManager as MicManager2
} from "audio_input";
import { TieState as TieState2 } from "score_data";
import { LiveNoteInfoDO, NoteInfoDO as NoteInfoDO2, SoundLibManager as SoundLibManager3 } from "sound_lib";
var _RhythmAssessor = class _RhythmAssessor extends Assessor {
  constructor(scoreViewer, visualizer) {
    super(scoreViewer, visualizer);
    //list of beat hit events
    this._keyMap = /* @__PURE__ */ new Map();
    this._isActive = false;
    this._defaultWindowSize = 0.25;
    this._onHit = (e) => {
      e.time = this._scoreViewer.playbackManager.qNoteTime;
      switch (e.hitType) {
        case HitType2.rhythm_hit_start:
          this._handleRhythmHitStart(e);
          break;
        case HitType2.rhythm_hit_end:
          this._handleRhythmHitEnd(e);
          break;
        case HitType2.beat_hit:
          if (this.assessBeats) {
            this._handleBeatHit(e);
          }
          break;
        case HitType2.rhythm_hit:
          this._handleRhythmHitStart(e);
          break;
        default:
          console.log("unsupported HitType");
      }
    };
    this._onScoreViewerEvent = (e) => {
      if (e.type === 0 /* noteReached */) {
        if (!this._firstNotePlayed)
          this._firstNotePlayed = true;
        this._notesReachedQue.push(e);
      }
    };
    this._onEnterFrame = () => {
      let cTime = this._slm.context.currentTime;
      let tempo = this._scoreViewer.playbackManager.tempo;
      let windowSize = this._defaultWindowSize * (tempo / _RhythmAssessor.DEFAULT_TEMPO);
      while (this._notesReachedIndex < this._notesReachedQue.length) {
        let ev = this._notesReachedQue[this._notesReachedIndex];
        if (ev.contextTime < cTime - windowSize) {
          this._notesReachedIndex++;
          let vng = ev.visualNoteGroup;
          if (vng.noteGroup.isRest || vng.noteGroup.isGrace) {
            continue;
          }
          let aNote = this.getAssessNoteFromVNG(vng);
          if (aNote != null && !aNote.matched) {
            this._markNoteIncorrect(aNote);
          }
        } else {
          break;
        }
      }
      if (this.assessBeats) {
        let qNoteTime = this._scoreViewer.playbackManager.qNoteTime;
        while (this._beatIndex < this._beatList.length) {
          let beat = this._beatList[this._beatIndex];
          if (beat.matched) {
            this._beatIndex++;
          } else if (beat.qNoteTime < qNoteTime - this._defaultWindowSize) {
            this._markBeatIncorrect(beat);
            this._beatIndex++;
          } else {
            break;
          }
        }
      }
    };
    this._initialize();
  }
  /**starts assessment*/
  start() {
    this._scoreViewer.event.on(this._onScoreViewerEvent);
    this._scoreViewer.vScore.pixiAppHelper.app.ticker.add(
      this._onEnterFrame
    );
    this._rhythmHits = [];
    this._beatHits = [];
    this._notesReachedIndex = 0;
    this._notesReachedQue = [];
    this._firstNotePlayed = false;
    this._noteIndex = 0;
    this._beatIndex = 0;
    if (this.usesKeyboard) {
      this._keyHitRecognizer.startHitRec();
      this._keyHitRecognizer.event.on(this._onHit);
      if (this.usesLiveNotes) {
        this._slm.enableLiveNotePlayer();
      }
    }
    if (this.usesMicHit) {
      this._micHitRecognizer.startHitRec();
      this._micHitRecognizer.event.on(this._onHit);
    }
    this._isActive = true;
  }
  /**stops assessment*/
  stop() {
    if (!this._isActive) {
      return;
    }
    if (this.usesKeyboard) {
      this._keyHitRecognizer.stopHitRec();
      this._keyHitRecognizer.event.off(this._onHit);
      this._slm.disableLiveNotePlayer();
    }
    if (this.usesMicHit) {
      this._micHitRecognizer.stopHitRec();
      this._micHitRecognizer.event.off(this._onHit);
    }
    this._scoreViewer.event.off(this._onScoreViewerEvent);
    this._scoreViewer.vScore.pixiAppHelper.app.ticker.remove(
      this._onEnterFrame
    );
  }
  setHitMode(mode) {
    this._hitMode = mode;
    this._keyHitRecognizer = KeyHitRecognizer2.get(this._slm.context);
    this._keyHitRecognizer.sendStartEndEvents = !this.usesMicHit;
    this._keyHitRecognizer.sendBeatEvents = this.assessBeats;
    if (this.usesMicHit) {
      if (!MediaDeviceManager2.isInitialized || !MediaDeviceManager2.get().isMicReady) {
        throw "You must initialize the MediaDeviceManager and get mic permission before using microphone rhythm assessment";
      }
      let inst = this.isClapHitRec ? AssessmentInstrumentUtils2.getInstrumentByID(101) : AssessmentInstrumentUtils2.getInstrumentByID(102);
      let micManager = MicManager2.get(this._slm.context);
      this._micHitRecognizer = MicHitRecognizer.get(micManager);
      this._micHitRecognizer.setInstrument(inst);
    }
  }
  get requiresMicrophone() {
    return this.usesMicHit;
  }
  _initialize() {
    this._slm = SoundLibManager3.get();
    this.setHitMode(0 /* keyboard */);
  }
  _handleRhythmHitStart(e) {
    this._rhythmHits.push(e);
    let note = this._findNoteMatchForHit(e);
    if (note != null) {
      this._markNoteCorrect(note);
    } else {
      note = this._findClosestNoteForHit(e);
      this._addExtraNote(e.time);
    }
    if (!this.usesMicHit) {
      this._playNote(note.vng.noteGroup.notes[0], e.keycode);
    }
  }
  _handleRhythmHitEnd(e) {
    if (this._keyMap.get(e.keycode) != null) {
      this._slm.stopLiveNote(this._keyMap.get(e.keycode), 0.2);
      this._keyMap.delete(e.keycode);
    }
  }
  _handleBeatHit(e) {
    this._beatHits.push(e);
    let beat = this._findBeatMatchForHit(e);
    if (beat != null) {
      this._markBeatCorrect(beat);
    } else {
      this._addExtraBeat(e.time);
    }
    this._playBeat();
  }
  _findNoteMatchForHit(e) {
    let tempo = this._scoreViewer.playbackManager.tempo;
    let windowSize = this._defaultWindowSize * (tempo / _RhythmAssessor.DEFAULT_TEMPO);
    for (let i = this._noteIndex; i < this._noteList.length; i++) {
      let cng = this._noteList[i].vng.noteGroup;
      if (cng.isRest || cng.notes[0].tieState === TieState2.CONTINUE || cng.notes[0].tieState === TieState2.STOP) {
        continue;
      }
      let noteTime = cng.qNoteTime;
      if (e.time > noteTime - windowSize && e.time < noteTime + windowSize) {
        this._noteIndex = i + 1;
        return this._noteList[i];
      } else if (e.time <= cng.qNoteTime - windowSize) {
        break;
      }
    }
    return null;
  }
  _findBeatMatchForHit(e) {
    for (let i = this._beatIndex; i < this._beatList.length; i++) {
      let beat = this._beatList[i];
      if (beat.matched) {
        continue;
      }
      if (e.time > beat.qNoteTime - this._defaultWindowSize && e.time < beat.qNoteTime + this._defaultWindowSize) {
        return this._beatList[i];
      } else if (e.time <= beat.qNoteTime - this._defaultWindowSize) {
        break;
      }
    }
    return null;
  }
  _findClosestNoteForHit(e) {
    let i = this._noteIndex < this._noteList.length ? this._noteIndex : this._noteList.length - 1;
    let closestNote = this._noteList[i];
    let closestDistance = Math.abs(closestNote.vng.noteGroup.qNoteTime - e.time);
    let increment = closestNote.vng.noteGroup.qNoteTime > e.time ? -1 : 1;
    for (i; i >= 0 && i < this._noteList.length; i += increment) {
      let distance = Math.abs(this._noteList[i].vng.noteGroup.qNoteTime - e.time);
      if (distance <= closestDistance) {
        closestDistance = distance;
        closestNote = this._noteList[i];
      } else {
        break;
      }
    }
    return closestNote;
  }
  _playNote(note, keyCode) {
    if (!this._slm.hasPreset(0, 0)) {
      throw "preset 0:0 not loaded!";
    }
    let part = note.noteGroup.voice.measure.staff.partRef;
    let pitch = ~~(note.displayCents / 100) - part.chromaticTransposition;
    let ldo = new LiveNoteInfoDO(pitch, 0.5, 0, 0);
    this._keyMap.set(keyCode, ldo);
    this._slm.startLiveNote(ldo);
  }
  _playBeat() {
    this._slm.playNotes([NoteInfoDO2.create(38, 0, 0.5, 0.5, 0.5, 128, 0)]);
  }
  get assessBeats() {
    return this._hitMode === 1 /* keyboardAndBeat */ || this._hitMode === 3 /* clapAndBeat */ || this._hitMode === 5 /* voiceAndBeat */;
  }
  get usesMicHit() {
    return this._hitMode === 2 /* clap */ || this._hitMode === 3 /* clapAndBeat */ || this._hitMode === 4 /* voice */ || this._hitMode === 5 /* voiceAndBeat */;
  }
  get isClapHitRec() {
    return this._hitMode === 2 /* clap */ || this._hitMode === 3 /* clapAndBeat */;
  }
  get isVoiceHitRec() {
    return this._hitMode === 4 /* voice */ || this._hitMode === 5 /* voiceAndBeat */;
  }
  get usesKeyboard() {
    return this._hitMode === 0 /* keyboard */ || this.assessBeats;
  }
  get usesLiveNotes() {
    return this._hitMode == 0 /* keyboard */;
  }
};
_RhythmAssessor.DEFAULT_TEMPO = 90;
var RhythmAssessor = _RhythmAssessor;
var RhythmAssessorHitMode = /* @__PURE__ */ ((RhythmAssessorHitMode2) => {
  RhythmAssessorHitMode2[RhythmAssessorHitMode2["keyboard"] = 0] = "keyboard";
  RhythmAssessorHitMode2[RhythmAssessorHitMode2["keyboardAndBeat"] = 1] = "keyboardAndBeat";
  RhythmAssessorHitMode2[RhythmAssessorHitMode2["clap"] = 2] = "clap";
  RhythmAssessorHitMode2[RhythmAssessorHitMode2["clapAndBeat"] = 3] = "clapAndBeat";
  RhythmAssessorHitMode2[RhythmAssessorHitMode2["voice"] = 4] = "voice";
  RhythmAssessorHitMode2[RhythmAssessorHitMode2["voiceAndBeat"] = 5] = "voiceAndBeat";
  return RhythmAssessorHitMode2;
})(RhythmAssessorHitMode || {});

// src/score_performance/audio/SFPerformanceNoteMaker.ts
import { NoteInfoDO as NoteInfoDO3 } from "sound_lib";
import {
  Dynamic,
  TempoTimeStampDO as TempoTimeStampDO2,
  PlaybackRegionDataType,
  TieState as TieState3,
  ArticulationType
} from "score_data";
var _SFPerformanceNoteMaker = class _SFPerformanceNoteMaker {
  constructor(soundLibManager) {
    this._slm = soundLibManager;
  }
  /**
   * creates playback notes for all notes in the passed in list of Parts
   * @param parts the Parts that contain the notes you want played
   * @param tempoRatio ratio of the tempo to the default tempo of the piece
   * @param playSoundFontNotes if true, notes will be played
   * @param metronomeVolume from 0 to 1.0
   * @param startNG NoteGroup with the qNoteTime where playback should begin
   * @param endQNoteTime the time when playback should stop. Notes that start
   * at this time will not be played.
   * @param startRepeatPass - Each time an element is encountered, the repeat
   * pass increments. 1 is the first pass.
   * @param endRepeatPass - Each time an element is encountered, the repeat
   * pass increments. 1 is the first pass. If left null, the final pass for
   * the endQNT will be used (matching end of song if endQNT is also null).
   * @param numCountoffBeats number of countoff beats that should be played
   * @param playRepeats if false, repeats will be ignored, creating a single
   * playback region from startNG to endQNoteTime
   */
  addNotesInParts(score, parts, {
    tempoRatio = 1,
    playSoundFontNotes = true,
    metronomeVolume = 0,
    startNG = null,
    endQNoteTime = null,
    startRepeatPass = 1,
    endRepeatPass = null,
    numCountoffBeats = 0,
    playRepeats = true
  }) {
    this._score = score;
    this._slm.clearSequence();
    this._tempoRatio = tempoRatio;
    let measures = parts[0].staves[0].measures;
    if (startNG == null) {
      startNG = measures[0].voices[0].noteGroups[0];
    }
    let startQNoteTime = this._getNoteOrStackTime(startNG);
    if (endQNoteTime == null) {
      endQNoteTime = measures[measures.length - 1].stack.endTime;
    }
    let regions = this._score.playbackInfo.getPlaybackRegions(
      startQNoteTime,
      endQNoteTime,
      startRepeatPass,
      endRepeatPass,
      playRepeats
    );
    let startTempo = regions[0].startTempo;
    let realStartQNoteTime = this._calculateQNoteStartTime(
      startQNoteTime,
      startNG.voice.measure,
      startTempo * this._tempoRatio,
      numCountoffBeats
    );
    this._createTempoTimeStamps(regions, realStartQNoteTime);
    this._createPlaybackNotes(parts, playSoundFontNotes, regions);
    this._createMetronomeBeats(metronomeVolume, regions);
    this._createCountoffClicks(
      numCountoffBeats,
      startTempo,
      realStartQNoteTime
    );
  }
  /**returns either the qNoteTime of the NoteGroup or the Stack it lies in,
   * whichever is greater (protection against grace notes)*/
  _getNoteOrStackTime(ng) {
    let stackTime = ng.voice.measure.stack.startTime;
    return ng.qNoteTime > stackTime ? ng.qNoteTime : stackTime;
  }
  _calculateQNoteStartTime(startQNoteTime, startMeasure, actualCountoffTempo, numCountoffBeats) {
    let beatType = startMeasure.beatType;
    if (beatType % 8 === 0 && actualCountoffTempo >= _SFPerformanceNoteMaker.COMPOUND_METER_TEMPO_CUTOFF) {
      this._qNotesPerCountoffBeat = 3 * (4 / beatType);
    } else {
      this._qNotesPerCountoffBeat = 4 / beatType;
    }
    if (!startMeasure.stack.isPickup) {
      this._countoffDuration = numCountoffBeats * this._qNotesPerCountoffBeat * 60 / actualCountoffTempo;
      startQNoteTime -= numCountoffBeats * this._qNotesPerCountoffBeat;
      return startQNoteTime;
    }
    let barEndTime = startMeasure.stack.endTime;
    let distToBar = barEndTime - startQNoteTime;
    let distToNearestBeat = distToBar;
    while (distToNearestBeat >= this._qNotesPerCountoffBeat) {
      distToNearestBeat -= this._qNotesPerCountoffBeat;
    }
    let adjustedCountoffBeats = numCountoffBeats - distToNearestBeat;
    this._countoffDuration = adjustedCountoffBeats * this._qNotesPerCountoffBeat * 60 / actualCountoffTempo;
    startQNoteTime -= adjustedCountoffBeats * this._qNotesPerCountoffBeat;
    return startQNoteTime;
  }
  _createTempoTimeStamps(regions, realStartQNoteTime) {
    let startTime = regions[0].startTime;
    this._tempoTimeStamps = this._score.playbackInfo.createTempoTimeStamps(
      regions
    );
    this._tempoTimeStamps.forEach((t) => {
      t.time = (t.time - startTime) / this._tempoRatio + this._countoffDuration;
      t.msTime = ~~(t.time * 1e3);
      t.tempo *= this._tempoRatio;
    });
    let countoffTimeStamp = new TempoTimeStampDO2(
      0,
      0,
      realStartQNoteTime,
      regions[0].startTempo * this._tempoRatio
    );
    countoffTimeStamp.qNoteEndTime = this._tempoTimeStamps[0].qNoteTime;
    countoffTimeStamp.nextSampleTimeStampDO = this._tempoTimeStamps[0];
    this._tempoTimeStamps.splice(0, 0, countoffTimeStamp);
  }
  _createPlaybackNotes(parts, playSoundFontNotes, regions) {
    let playStartTime = regions[0].startTime;
    let playAllParts = parts.length === this._score.parts.length;
    let partDynamics = /* @__PURE__ */ new Map();
    for (let reg of regions) {
      for (let i = 0; i < reg.data.length; i++) {
        let data = reg.data[i];
        if (data.measureAttachment != null) {
          let att = data.measureAttachment;
          if (att instanceof Dynamic)
            partDynamics.set(att.measure.staff.partRef, att.volume);
        }
        if (data.type !== PlaybackRegionDataType.NOTE_GROUP)
          continue;
        let part = data.noteGroup.voice.measure.staff.partRef;
        let partIndex = parts.indexOf(part);
        if (!playAllParts && partIndex === -1)
          continue;
        if (part.midiChannel % 16 !== 10) {
          this._slm.setPreset(0, part.midiPreset);
        } else {
          this._slm.setPreset(128, 0);
        }
        let ng = data.noteGroup;
        let notes = ng.notes;
        let numNotes = notes.length;
        let startTime = (data.time - playStartTime) / this._tempoRatio + this._countoffDuration;
        if (startTime < 0) {
          startTime = 0;
        }
        ng.playbackStartTime = startTime;
        ng.playbackTempo = data.tempo * this._tempoRatio;
        if (part.muted || !playSoundFontNotes) {
          continue;
        }
        let ngQnDur = ng.qNoteTime + ng.qNoteDuration <= reg.qNoteEndTime ? ng.qNoteDuration : reg.qNoteEndTime - ng.qNoteTime;
        let duration = !ng.isGrace ? ngQnDur * (60 / (data.tempo * this._tempoRatio)) : 0.25;
        let tieDuration = 0;
        for (let j = 0; j < numNotes; j++) {
          let note = notes[j];
          if (note.tieState !== TieState3.NONE) {
            if (note.tieState === TieState3.START) {
              let tieEndFound = false;
              tieDuration = 0;
              let nextNG = ng.next;
              while (!tieEndFound && nextNG != null && nextNG.qNoteTime < reg.qNoteEndTime) {
                let nextNote = nextNG.notes.find((n) => n.displayCents === note.displayCents);
                if (nextNote == null) {
                  break;
                }
                let nextNgData;
                for (let j2 = i + 1; j2 < reg.data.length; j2++) {
                  if ((nextNgData = reg.data[j2]).noteGroup == nextNG) {
                    break;
                  }
                }
                if (nextNote.tieState === TieState3.STOP) {
                  tieEndFound = true;
                  tieDuration += nextNG.qNoteDuration * (60 / (nextNgData.tempo * this._tempoRatio));
                  break;
                } else if (nextNote.tieState === TieState3.CONTINUE) {
                  tieDuration += nextNG.qNoteDuration * (60 / (nextNgData.tempo * this._tempoRatio));
                }
                nextNG = nextNG.next;
              }
            } else {
              continue;
            }
          }
          let noteVolume = (partDynamics.get(part) ?? 0.4) * part.volume;
          if (ng.articulations != null) {
            for (let art of ng.articulations) {
              if (art.type === ArticulationType.ACCENT) {
                noteVolume += 0.2;
                if (noteVolume > 1)
                  noteVolume = 1;
              } else if (art.type === ArticulationType.STACCATO) {
                duration *= 0.75;
              }
            }
          }
          let pitch;
          if (note.playbackCents <= 0) {
            pitch = ~~(note.displayCents / 100) - part.chromaticTransposition + 12 * part.playbackOctaveDelta;
          } else {
            pitch = ~~(note.playbackCents / 100);
          }
          let ndo = NoteInfoDO3.create(
            pitch,
            startTime,
            duration + tieDuration,
            noteVolume,
            part.pan
          );
          ndo.trackId = ng.trackId != null ? ng.trackId : partIndex + 1;
          this._slm.addNoteToSequence(ndo);
        }
      }
    }
  }
  _createMetronomeBeats(metronomeVolume, regions) {
    this._beats = [];
    this._slm.setPreset(128, 0);
    let playStartTime = regions[0].startTime;
    let useSubBeat = false;
    let cStack;
    for (let region of regions) {
      for (let data of region.data) {
        if (data.stack != null) {
          cStack = data.stack;
          useSubBeat = cStack.measures[0].beatType % 8 === 0 && data.tempo * this._tempoRatio < _SFPerformanceNoteMaker.COMPOUND_METER_TEMPO_CUTOFF;
        }
        if (data.type === PlaybackRegionDataType.BEAT || useSubBeat && data.type === PlaybackRegionDataType.SUB_BEAT) {
          this._beats.push(data.qNoteTime);
          let beatTime = (data.time - playStartTime) / this._tempoRatio + this._countoffDuration;
          let duration = 60 / data.tempo * data.qNoteDuration / this._tempoRatio;
          let metronomeNote;
          let actualMetVolume;
          if (data.qNoteTime !== cStack.startTime) {
            metronomeNote = 37;
            actualMetVolume = metronomeVolume * 0.8;
          } else {
            metronomeNote = 37;
            actualMetVolume = metronomeVolume;
          }
          let ndo = NoteInfoDO3.create(
            metronomeNote,
            beatTime,
            duration,
            actualMetVolume,
            0.5
          );
          ndo.trackId = 0;
          this._slm.addNoteToSequence(ndo);
        }
      }
    }
  }
  _createCountoffClicks(numCountoffBeats, startNGMarkerTempo, startQNoteTime) {
    this._slm.setPreset(128, 0);
    let remainingClicks = numCountoffBeats;
    let clickPos = 0;
    let cSecondsPerQNote = 1 / (startNGMarkerTempo * this._tempoRatio / 60);
    let clickDuration = this._qNotesPerCountoffBeat * cSecondsPerQNote;
    this._countoffBeats = [];
    let qNoteTime = startQNoteTime;
    while (remainingClicks > 0) {
      let ndo = NoteInfoDO3.create(37, clickPos, clickDuration, 0.8, 0.5);
      ndo.trackId = 0;
      this._slm.addNoteToSequence(ndo);
      clickPos += clickDuration;
      remainingClicks -= 1;
      this._countoffBeats.push(qNoteTime);
      qNoteTime += this._qNotesPerCountoffBeat;
    }
  }
  get sampleTimeStamps() {
    return this._tempoTimeStamps.slice();
  }
  /**
   * gets the qNote positions of the beats in the playback region (from 
   * startNG to endNG)
   */
  get beatQNotePositions() {
    return this._beats;
  }
  /**
   * gets the qNote positions of the beats in the countoff
   */
  get countoffBeatQNotePositions() {
    return this._countoffBeats;
  }
  /**the duration of the countoff in seconds*/
  get countoffDuration() {
    return this._countoffDuration;
  }
};
//at this tempo, beats and clicks will be sent once every 3 in compound
//meters instead of each beat
_SFPerformanceNoteMaker.COMPOUND_METER_TEMPO_CUTOFF = 80;
var SFPerformanceNoteMaker = _SFPerformanceNoteMaker;

// src/score_performance/drivers/sf_loop_driver/SFLoopDriver.ts
import {
  AudioRecorder2,
  MediaDeviceManager as MediaDeviceManager3,
  MicManager as MicManager3
} from "audio_input";
import { DestinationAudioComponent } from "audio_output";
import { AudioLooper } from "audio_sequencer";
import {
  MusicXmlParser2,
  ScoreFormatter
} from "score_data";
import { BitmapMusicRenderer, MusicTextures as MusicTextures2 } from "score_render";
import { SoundLibEventType, SoundLibManager as SoundLibManager5 } from "sound_lib";

// src/score_performance/drivers/SFPlaybackDO.ts
var SFPlaybackDO = class _SFPlaybackDO {
  constructor() {
    /**The tempo ratio, with 1.0 being default.*/
    this.tempoRatio = 1;
    /**If true, SoundFonts will be used to play score notes*/
    this.playSoundFontNotes = true;
    /**Volume for the metronome - should be between 0 and 1.0*/
    this.metronomeVolume = 1;
    /**The desired length of the countoff in seconds. If countoffBeats is
     * also defined, it will override this. The actual duration of the countoff
     * will be calculated and reported via countoffDuration.*/
    this.countoffSeconds = 2;
    /**if true, repeats in the score are honored*/
    this.playRepeats = true;
    /**The [AssessmentType] used for this playback session*/
    this.assessmentType = 0 /* none */;
    /**if true, mic input will be recorded*/
    this.record = false;
    /**if true, an [AudioSequencer] will be used to play any audio files added
     * via addSequencerSounds()*/
    this.playAudio = false;
  }
  /**creates a new [SFPlaybackDO] by copying relevant settings from an
   * existing source [SFPlaybackDO] object.*/
  static fromPDO(source) {
    let pdo = new _SFPlaybackDO();
    pdo.tempoRatio = source.tempoRatio;
    pdo.playSoundFontNotes = source.playSoundFontNotes;
    pdo.metronomeVolume = source.metronomeVolume;
    pdo.startNG = source.startNG;
    pdo.endQNoteTime = source.endQNoteTime;
    pdo.countoffBeats = source.countoffBeats;
    pdo.countoffSeconds = source.countoffSeconds;
    pdo.playRepeats = source.playRepeats;
    pdo.assessmentType = source.assessmentType;
    pdo.instDO = source.instDO;
    pdo.record = source.record;
    pdo.playAudio = source.playAudio;
    pdo._countoffDuration = source._countoffDuration;
    pdo._startNoteTimeOffset = source._startNoteTimeOffset;
    pdo._beatQNotePositions = source._beatQNotePositions.slice(0);
    pdo._countoffBeatQNotePositions = source._countoffBeatQNotePositions;
    return pdo;
  }
  /**Calculated duration of the countoff, in seconds.*/
  get countoffDuration() {
    return this._countoffDuration;
  }
  /**Determines whether playback requires the microphone for recording or
   * assessment*/
  get requiresMic() {
    return this.record || this.assessmentType === 3 /* pitch */ || this.assessmentType === 2 /* rhythmClap */;
  }
};

// src/score_performance/drivers/sf_loop_driver/SFLoopDriver.ts
var SFLoopDriver = class _SFLoopDriver {
  constructor() {
  }
  /**creates a new [SFLoopDriver]
   * @param context - an [AudioContext] instance
   * @param config - [SFLoopDriverConfig] object with path settings for music
   * symbols and SoundFonts. See class for requirements info.
   * @param instrument the default instrument - if null, the first instrument
   * in [InstrumentUtils] will be used.
   * @returns a Promise that completes with the new [SFLoopDriver] instance.*/
  static async create(pixiApp, context, config) {
    let sfd = new _SFLoopDriver();
    sfd._pah = pixiApp;
    sfd._context = context;
    if (sfd._context == null)
      throw "AudioContext not set";
    await MusicTextures2.createTextures(config.initialPixelsPerSpace);
    if (!MediaDeviceManager3.isInitialized) {
      let deviceManager = MediaDeviceManager3.get();
      await deviceManager.init();
    }
    sfd._micManager = MicManager3.get(sfd._context);
    sfd.setInstrument(config.initialInstrument);
    sfd._outputComponent = config.outputComponent ?? new DestinationAudioComponent(context);
    sfd._slm = SoundLibManager5.get(
      context,
      config.soundLibType,
      sfd._outputComponent
    );
    if (config.indexedDbName != null) {
      sfd._slm.dbName = config.indexedDbName;
    }
    await sfd._slm.loadSoundMap(config.soundMapPath);
    await sfd._slm.loadPresets(null);
    sfd._performanceNoteMaker = new SFPerformanceNoteMaker(sfd._slm);
    sfd._renderer = new BitmapMusicRenderer(pixiApp);
    if (config.useAudioLooper) {
      sfd._audioLooper = new AudioLooper(context);
      sfd._audioLooper.connect(sfd._outputComponent);
    }
    sfd._audioRecorder2 = new AudioRecorder2(sfd._micManager);
    return sfd;
  }
  setInstrument(instDO) {
    if (instDO != null) {
      this._instrument = instDO;
    }
  }
  /**attempts to enable microphone, if one is available. User is prompted
   * to give permission the first session.*/
  async enableMicrophone(micOptions = null, videoOptions = null) {
    let deviceManager = MediaDeviceManager3.get();
    let useCamera = videoOptions != null;
    await deviceManager.getPermissions(true, useCamera);
    await deviceManager.getDevices();
    if (deviceManager.currentMic == null) {
      deviceManager.setMic();
    }
    if (useCamera && deviceManager.currentCamera == null) {
      deviceManager.setCamera();
    }
    await this._micManager.initMicMediaStream(micOptions, videoOptions);
  }
  async parseScore(musicXmlString, overrideScoreProps = null) {
    let parser = new MusicXmlParser2();
    if (overrideScoreProps != null) {
      this._score = await parser.parseXML(
        musicXmlString,
        true,
        true,
        overrideScoreProps
      );
    } else {
      this._score = await parser.parseXML(musicXmlString, true, false);
    }
    return this._score;
  }
  renderNotation(score, width, height, parts = null) {
    this._score = score;
    this._score.getSystems().forEach((s) => s.indent = 0);
    return this._renderMusic(width, height, parts);
  }
  resetNotation(width = 0, height = 0, parts = null) {
    if (width === 0)
      width = this._scoreViewerWidth;
    if (height === 0)
      height = this._scoreViewerHeight;
    if (this._viewer != null) {
      this._viewer.vScore.prepareForRemoval();
    }
    if (parts == null)
      parts = this._displayedParts;
    this._renderMusic(width, height, parts);
  }
  /**Loads and renders a score all in one action*/
  async loadScore(musicXmlString, pageWidth, pageHeight, {
    properties = null,
    allowMultipleSystems = true
  }) {
    if (this._slm.isPlaying && this._soundLibEventCallback != null) {
      this._stopNotationPlayback(false);
    }
    let parser = new MusicXmlParser2();
    this._score = await parser.parseXML(
      musicXmlString,
      true,
      true,
      properties
    );
    this._score.setPageSize(
      pageWidth,
      this._score.scoreProperties.pageHeight
    );
    let formatter = new ScoreFormatter(this._score);
    formatter.setPartList([this._score.parts[0]]);
    formatter.formatScore2(allowMultipleSystems);
    this._vScore = this._renderer.renderScore(
      this._score,
      [this._score.parts[0]]
    );
    if (this._viewer != null) {
      this._viewer.setScore(this._vScore);
      this._viewer.setWidthAndHeight(pageWidth, pageHeight);
    } else {
      this._viewer = new ScoreViewer3(
        this._pah,
        this._vScore,
        pageWidth,
        pageHeight,
        this._context
      );
    }
    return this._viewer;
  }
  unloadScore() {
    if (this._assessor != null) {
      this._assessor.prepareForRemoval();
      this._assessor = null;
    }
    if (this._viewer != null) {
      this._viewer.prepareForRemoval();
      this._viewer.parent?.removeChild(this._viewer);
      this._viewer = null;
    }
    this._vScore = null;
    this._score = null;
  }
  /**adds a [LoopTrack] to the [AudioLooper]. If the looper is already
   * playing, the track is added to the playback.*/
  addLoopTrack(track) {
    if (this._audioLooper == null) {
      throw "AudioLooper must be enabled in SfLoopDriverConfig";
    }
    this._audioLooper.addTrack(track);
  }
  /**removes a [LoopTrack] from the [AudioLooper]*/
  removeLoopTrack(track) {
    this._audioLooper.removeTrack(track);
  }
  /**removes all [LoopTrack]s from the [AudioLooper]*/
  removeAllLoopTracks() {
    this._audioLooper.removeAllTracks();
  }
  /**adds [NoteInfoDO] notes to the [SoundLibManager] for playback and returns
   * the id of the note set that is created for them.*/
  addLoopNotes(notes) {
    this._slm.clearSequence();
    for (let note of notes) {
      this._slm.addNoteToSequence(note);
    }
    return this._slm.prepareSequencedPlayback(true);
  }
  /**removes a note set from the player*/
  removeLoopNotes(notesetId) {
    this._slm.removeNoteSet(notesetId);
  }
  /**removes all note sets from the player*/
  removeAllLoopNotes() {
    this._slm.clearNotes();
  }
  /**starts loop playback for the passed in [notes]. If the [AudioLooper]
   * was enabled in the [SFLoopDriverConfig], it will start playing as well.
   * Set the [loopDuration] to the full duration of the loop, in seconds.*/
  startLoopPlayback(loopDuration, { startTime = null }) {
    if (!this._slm.isPlaying) {
      this._slm.play(startTime ?? 0);
    } else if (startTime != null) {
      let delta = startTime - this._slm.currentPlayTime;
      this._slm.shiftPlayerTime(delta);
    }
    if (this._audioLooper != null) {
      this._audioLooper.play(0, loopDuration, {
        overridePlayStartTime: this._slm.playStartTime
      });
    }
  }
  adjustLoopTime(timeDelta) {
    this._audioLooper.stop();
    this._slm.shiftPlayerTime(timeDelta);
    this._audioLooper.play(0, this._audioLooper.masterLoopDuration, {
      overridePlayStartTime: this._slm.playStartTime
    });
  }
  stop() {
    if (this._slm.isPlaying) {
      this._slm.stop();
      this._audioLooper?.stop();
      this._stopNotationPlayback(false);
    }
  }
  startFreetimeAssessment(tempo) {
    if (this._sfPlayerNoteSetId != null) {
      return;
    }
    let ft = new FreeTimeRhythmAssessor(this._viewer);
    ft.tempo = tempo;
    this._assessor = ft;
    this._assessor.start();
  }
  /**Generates [NoteInfoDO]s for the notation, adding them to the
   * [SoundManager]s note builder sequence. To play these notes,
   * [SoundLibManager.prepareSequencedPlayback] must be called. If you call
   * [playNotation], this is handled for you.*/
  prepareNotationPlayback({
    tempoRatio = 1,
    playSoundFontNotes = true,
    metronomeVolume = 1,
    startNG = null,
    endQNoteTime = null,
    countoffBeats = null,
    countoffSeconds = null,
    assessmentType = 0 /* none */,
    instDO = null,
    record = false
  }) {
    if (countoffBeats == null) {
      if (countoffSeconds != null) {
        countoffBeats = this._getCountoffBeatsForSeconds(
          countoffSeconds,
          startNG,
          tempoRatio
        );
      } else {
        countoffBeats = 0;
      }
    }
    this._performanceNoteMaker.addNotesInParts(
      this._score,
      this._score.parts,
      {
        tempoRatio,
        playSoundFontNotes,
        metronomeVolume,
        startNG,
        endQNoteTime,
        startRepeatPass: 1,
        endRepeatPass: null,
        numCountoffBeats: countoffBeats,
        playRepeats: false
      }
    );
    let pdo = new SFPlaybackDO();
    pdo._countoffDuration = this._performanceNoteMaker.countoffDuration;
    pdo._beatQNotePositions = this._performanceNoteMaker.beatQNotePositions;
    pdo._countoffBeatQNotePositions = this._performanceNoteMaker.countoffBeatQNotePositions;
    pdo.tempoRatio = tempoRatio;
    pdo.playSoundFontNotes = playSoundFontNotes;
    pdo.metronomeVolume = metronomeVolume;
    pdo.startNG = startNG;
    pdo.endQNoteTime = endQNoteTime;
    pdo.countoffBeats = countoffBeats;
    pdo.assessmentType = assessmentType;
    pdo.record = record;
    if (instDO != null) {
      this.setInstrument(instDO);
    }
    this._prepareAssessment(assessmentType);
    return pdo;
  }
  /**adds notation [PerformanceNoteDO]s as a set to the [SFPlayer]. The
   * [startTime] parameter is used for delaying the start of playback,
   * typically to line up with a loop. The time is in terms of seconds from
   * the start of [SoundLibManager] playback. Use 
   * [SoundLibManager.playStartTime] to calculate this. [startLoopPlayback] 
   * must be called before using this. The optional [completeCallback] will be
   * called upon playback completion. If a value for [extraRecordTime] is 
   * passed in, this calls to stop notation playback and recording and sending
   * the [completeCallback] will be delayed by this amount (in seconds).*/
  playNotation(pdo, {
    startTime = null,
    completeCallback = null,
    extraRecordTime = null
  }) {
    if (!this._slm.isPlaying)
      throw "must call startLoopPlayback() first";
    if (this._soundLibEventCallback != null) {
      this._stopNotationPlayback(false);
    }
    if (startTime == null) {
      startTime = this._slm.currentPlayTime;
    }
    this._sfPlayerNoteSetId = this._slm.prepareSequencedPlayback(
      false,
      startTime
    );
    this._notationPlaybackStartTime = this._context.currentTime;
    let startDelay = startTime - this._slm.currentPlayTime;
    if (startDelay < 0)
      startDelay = 0;
    this._viewer.play(
      this._performanceNoteMaker.sampleTimeStamps,
      pdo._beatQNotePositions,
      pdo._countoffBeatQNotePositions,
      startDelay,
      pdo.startNG,
      pdo.endQNoteTime
    );
    if (pdo.record) {
      this._audioRecorder2.record();
    }
    if (this._assessor != null) {
      this._assessor.start();
      if (this._assessor.requiresMicrophone) {
        this._micManager.startCapture();
      }
    }
    this._slm.soundLibEvent.on(
      this._soundLibEventCallback = (e) => {
        if (e.type === SoundLibEventType.noteSetComplete && this._slm.isPlaying && e.noteSetId === this._sfPlayerNoteSetId) {
          if (extraRecordTime != null) {
            setTimeout(() => {
              this._stopNotationPlayback(true);
              if (completeCallback != null) {
                completeCallback();
              }
            }, extraRecordTime * 1e3);
          } else {
            this._stopNotationPlayback(true);
            if (completeCallback != null) {
              completeCallback();
            }
          }
        }
      }
    );
  }
  /**stops notation playback*/
  stopNotation() {
    this._stopNotationPlayback(false);
  }
  clearAssessmentVisuals() {
    if (this._assessor != null) {
      this._assessor.clearAssessmentVisuals();
    }
  }
  /**gets recording as an html.Blob object that can be saved to a file.*/
  getRecordingBlobAR2() {
    return this._audioRecorder2.getAudioBlob();
  }
  /**returns the AudioContext time, in seconds, when recording starts.*/
  get recordStartTime() {
    return this._audioRecorder2.recordStartTime;
  }
  /**returns the AudioContext time, in seconds, when recording stops.*/
  get recordStopTime() {
    return this._audioRecorder2.recordStopTime;
  }
  /**returns the AudioContext time, in seconds, when the notation notes
   * are sent to the [SoundLibManager] for playback via [playNotation]. This 
   * does not account for any delayed start that may be passed to 
   * [playNotation] via the startTime parameter.*/
  get notationPlaybackStartTime() {
    return this._notationPlaybackStartTime;
  }
  /**Gets an AudioElement with the recorded audio. This uses AudioRecorder2.*/
  getAudioElementAR2() {
    return this._audioRecorder2.getAudioElement();
  }
  _stopNotationPlayback(completePerformance) {
    if (this._viewer != null) {
      this._viewer.stop();
    }
    if (this._assessor != null) {
      this._assessor.stop();
      if (this._assessor.requiresMicrophone) {
        this._micManager.stopCapture();
      }
      if (completePerformance) {
      }
    }
    if (this._audioRecorder2.isRecording) {
      this._audioRecorder2.stop();
    }
    if (this._soundLibEventCallback != null) {
      this._slm.soundLibEvent.off(this._soundLibEventCallback);
      this._slm.removeNoteSet(this._sfPlayerNoteSetId);
      this._soundLibEventCallback = null;
      this._sfPlayerNoteSetId = null;
    }
  }
  _prepareAssessment(assessmentType) {
    if (assessmentType === 3 /* pitch */) {
      this._assessor = new PitchAssessor(this._viewer);
      this._assessor.setInstrument(this._instrument);
    } else if (assessmentType === 1 /* rhythmKeyboard */) {
      this._assessor = new RhythmAssessor(this._viewer);
    } else {
      this._assessor = null;
    }
  }
  _renderMusic(width, height, parts) {
    this._markMeasuresForRendering();
    this._score.setPageSize(width, this._score.scoreProperties.pageHeight);
    this._formatMusic(parts);
    let vScore = this._renderer.renderScore(this._score, parts);
    this._displayedParts = parts;
    this._scoreViewerWidth = width;
    this._scoreViewerHeight = height;
    if (this._viewer != null) {
      this._viewer.setScore(vScore);
      this._viewer.setWidthAndHeight(width, height);
    } else {
      this._viewer = new ScoreViewer3(
        this._pah,
        vScore,
        width,
        height,
        this._context
      );
    }
    return this._viewer;
  }
  _markMeasuresForRendering() {
    let stacks = this._score.getMeasureStacks();
    for (let stack of stacks) {
      for (let measure of stack.measures) {
        measure.notesNeedRendering = true;
      }
    }
  }
  _formatMusic(parts) {
    let formatter = new ScoreFormatter(this._score);
    formatter.setPartList(parts);
    formatter.formatScore2(true, 0);
  }
  _getCountoffBeatsForSeconds(seconds, startNG, tempoRatio) {
    if (startNG == null) {
      startNG = this._score.parts[0].staves[0].measures[0].voices[0].noteGroups[0];
    }
    let stack = startNG.voice.measure.stack;
    let tm;
    let tmList = stack.tempoMarkers;
    if (tmList != null) {
      for (let i = tmList.length - 1; i >= 0; i--) {
        if (tmList[i].qNoteTime <= startNG.qNoteTime) {
          tm = tmList[i];
        }
      }
    } else {
      tm = stack.getInheritedTempoMarker();
    }
    let tempo = tm != null ? tm.tempo : 90;
    let actualTempo = tempo * tempoRatio;
    let startMeasure = startNG.voice.measure;
    let beatType = startMeasure.beatType;
    let qNotesPerMeasure = startMeasure.numBeats * 4 / beatType;
    let secondsPerMeasure = qNotesPerMeasure * 60 / actualTempo;
    let numCountoffMeasures = Math.ceil(seconds / secondsPerMeasure);
    seconds = numCountoffMeasures * secondsPerMeasure;
    let qNotesPerCountoffBeat;
    if (beatType % 8 === 0 && actualTempo >= SFPerformanceNoteMaker.COMPOUND_METER_TEMPO_CUTOFF) {
      qNotesPerCountoffBeat = 3 * (4 / beatType);
    } else {
      qNotesPerCountoffBeat = 4 / beatType;
    }
    let numBeats = seconds / (qNotesPerCountoffBeat * (60 / actualTempo));
    return Math.round(numBeats);
  }
  get pixiAppHelper() {
    return this._pah;
  }
  get soundLibManager() {
    return this._slm;
  }
  get audioLooper() {
    return this._audioLooper;
  }
  get isPlaying() {
    return this._slm.isPlaying;
  }
  get assessor() {
    return this._assessor;
  }
  /**The audio output from the [SFScoreDriver] will be routed through the
   * [outputComponent]. This defaults to an instance of [MasterAudioComponent]
   * and can be changed via the [SFLoopDriverConfig] during initialization.*/
  get outputComponent() {
    return this._outputComponent;
  }
  get score() {
    return this._score;
  }
  get scoreViewer() {
    return this._viewer;
  }
  get audioContext() {
    return this._context;
  }
  /**The MicrophoneManager instance*/
  get micManager() {
    return this._micManager;
  }
};

// src/score_performance/drivers/sf_loop_driver/SFLoopDriverConfig.ts
import { ScoreProperties as ScoreProperties3, InstrumentUtils } from "score_data";
var SFLoopDriverConfig = class {
  constructor(soundLibType, soundMapPath) {
    /**The initial scaling for the score. Used for initial texture generation.
     * If you pass in a [ScoreProperties] object when parsing your score, its
     * [ScoreProperties.pixelsPerInch] property should match this.*/
    this.initialPixelsPerSpace = ScoreProperties3.DEFAULT_PIXELS_PER_SPACE;
    /**the initial instrument to use.*/
    this.initialInstrument = InstrumentUtils.instruments[0];
    /**if true, an [AudioLooper] will be initialized*/
    this.useAudioLooper = false;
    this.soundLibType = soundLibType;
    this.soundMapPath = soundMapPath;
  }
};

// src/score_performance/drivers/sf_score_driver/SFScoreDriver.ts
import {
  AudioRecorder,
  AudioRecorder2 as AudioRecorder22,
  MediaDeviceManager as MediaDeviceManager4,
  MicManager as MicManager4
} from "audio_input";
import { DestinationAudioComponent as DestinationAudioComponent2 } from "audio_output";
import { AudioSequencer } from "audio_sequencer";
import { SoundLibEventType as SoundLibEventType2, SoundLibManager as SoundLibManager6 } from "sound_lib";

// src/score_performance/drivers/sf_score_driver/SFDriverDrawManager.ts
import { ScoreFormatter as ScoreFormatter2, ScoreProperties as ScoreProperties4 } from "score_data";
var SFDriverDrawManager = class {
  constructor(driver) {
    this._scoreViewerWidth = 0;
    this._scoreViewerHeight = 0;
    /**Affects the number of measures that fit on a line, effectively adjusting
     * the scaling. The actual width of the rendered music is controlled
     * separately. Default is ScoreProperties.DEFAULT_PAGE_WIDTH.*/
    this.defaultPageWidth = ScoreProperties4.DEFAULT_PAGE_WIDTH;
    this._driver = driver;
  }
  /**
   * renders a new score for the first time
   */
  renderNotation(width, height, parts = null, measuresPerSystem = 0, clearSystemIndents = false) {
    if (clearSystemIndents) {
      this._clearSystemIndents();
    }
    this._scoreFormatter = null;
    this._renderMusic(width, height, measuresPerSystem, parts);
    return this._driver._scoreViewer;
  }
  /**
   * re-renders the score and adds the new VisualScore to the existing 
   * ScoreViewer
   */
  resetNotation(width = 0, height = 0, measuresPerSystem = 0, parts = null) {
    if (width === 0)
      width = this._scoreViewerWidth;
    if (height === 0)
      height = this._scoreViewerHeight;
    if (this._driver._scoreViewer != null) {
      this._driver._scoreViewer.vScore.prepareForRemoval();
    }
    if (parts == null)
      parts = this._displayedParts;
    this._renderMusic(width, height, measuresPerSystem, parts);
  }
  /**
   * resizes the scoreViewer visible area - if values are left null,
   * existing width and/or height is used
   */
  resizeScoreViewer(width, height) {
    if (width != null) {
      this._scoreViewerWidth = width;
    }
    if (height != null) {
      this._scoreViewerHeight = height;
    }
    if (this._driver._scoreViewer != null) {
      this._driver._scoreViewer.setWidthAndHeight(
        this._scoreViewerWidth,
        this._scoreViewerHeight
      );
    }
  }
  _renderMusic(width, height, measuresPerSystem, parts) {
    this._markMeasuresForRendering();
    let score = this._driver._score;
    let pageWidth = width;
    score.setPageSize(pageWidth, score.scoreProperties.pageHeight);
    this._formatMusic(parts, measuresPerSystem);
    let vScore = this._driver._renderer.renderScore(
      this._driver._score,
      parts
    );
    this._displayedParts = parts;
    this._scoreViewerWidth = width;
    this._scoreViewerHeight = height;
    if (this._driver._scoreViewer != null) {
      this._driver._scoreViewer.setScore(vScore);
      this._driver._scoreViewer.setWidthAndHeight(width, height);
    } else {
      this._driver._scoreViewer = new ScoreViewer3(
        this._driver._pah,
        vScore,
        this._scoreViewerWidth,
        this._scoreViewerHeight,
        this._driver._context
      );
    }
  }
  _markMeasuresForRendering() {
    let stacks = this._driver._score.getMeasureStacks();
    for (let i = 0; i < stacks.length; i++) {
      let stack = stacks[i];
      let measures = stack.measures;
      for (let j = 0; j < measures.length; j++) {
        measures[j].notesNeedRendering = true;
      }
    }
  }
  _clearSystemIndents() {
    let systems = this._driver._score.getSystems();
    for (let system of systems) {
      system.indent = 0;
    }
  }
  /**
   * formats the music for the requested parts
   * @param	parts the parts to format - if null, all parts are included in 
   * the formatting
   */
  _formatMusic(parts, measuresPerSystem = 0) {
    if (this._scoreFormatter == null) {
      this._scoreFormatter = new ScoreFormatter2(this._driver._score);
    }
    this._scoreFormatter.setPartList(parts);
    this._scoreFormatter.formatScore2(true, measuresPerSystem);
  }
};

// src/score_performance/drivers/sf_score_driver/SFScoreDriver.ts
import {
  MusicXmlParser2 as MusicXmlParser22
} from "score_data";
import { BitmapMusicRenderer as BitmapMusicRenderer2, MusicTextures as MusicTextures3 } from "score_render";

// src/score_performance/drivers/sf_score_driver/assessor_factory.ts
var IAssessorFactory = class {
};
var BasicAssessorFactory = class extends IAssessorFactory {
  getAssessor(assessType, scoreViewer) {
    switch (assessType) {
      case 3 /* pitch */:
        return new PitchAssessor(
          scoreViewer,
          new NewAssessmentVisualizer()
        );
      case 1 /* rhythmKeyboard */:
        return new RhythmAssessor(
          scoreViewer,
          new NewAssessmentVisualizer()
        );
      case 0 /* none */:
        return null;
      default:
        throw "unhandled AssessmentType: ${assessType.toString()}";
    }
  }
};

// src/score_performance/drivers/sf_score_driver/sf_driver_events.ts
var SFDriverStatusEvent = class {
  constructor(type) {
    this.type = type;
  }
};
var SFDriverStatusType = /* @__PURE__ */ ((SFDriverStatusType2) => {
  SFDriverStatusType2[SFDriverStatusType2["textureLoading"] = 0] = "textureLoading";
  SFDriverStatusType2[SFDriverStatusType2["sfDownloadProgress"] = 1] = "sfDownloadProgress";
  SFDriverStatusType2[SFDriverStatusType2["sfParsing"] = 2] = "sfParsing";
  SFDriverStatusType2[SFDriverStatusType2["scoreParsing"] = 3] = "scoreParsing";
  SFDriverStatusType2[SFDriverStatusType2["scoreRendering"] = 4] = "scoreRendering";
  return SFDriverStatusType2;
})(SFDriverStatusType || {});

// src/score_performance/drivers/sf_score_driver/SFScoreDriver.ts
import { createEmitter as createEmitter4 } from "ts_utils";
var SFScoreDriver = class _SFScoreDriver {
  constructor() {
    this._useAudioRecorder2 = false;
    this._isPlaying = false;
    this._assessorFactory = new BasicAssessorFactory();
    this._leaveMicCaptureEnabled = false;
    this._statusEmitter = createEmitter4();
    this._onSoundLibEvent = (e) => {
      switch (e.type) {
        case SoundLibEventType2.loadProgress:
          let status = new SFDriverStatusEvent(
            1 /* sfDownloadProgress */
          );
          status.progress = e.progress;
          this._statusEmitter.emit(status);
          break;
        case SoundLibEventType2.parsing:
          this._statusEmitter.emit(new SFDriverStatusEvent(
            2 /* sfParsing */
          ));
          break;
        case SoundLibEventType2.libraryReady:
          break;
        case SoundLibEventType2.noteSetComplete:
          break;
        case SoundLibEventType2.playbackFinished:
          this._onPlaybackComplete();
          break;
        default:
      }
    };
    this._onPlaybackComplete = () => {
      this.stop();
      if (this._playbackCompleteCallback != null) {
        this._playbackCompleteCallback();
      }
    };
  }
  /**listen for progress in downloading SF, parsing SF, parsing XML, and
   * rendering scores.*/
  get statusEvent() {
    return this._statusEmitter.event;
  }
  /**creates a new SFScoreDriver
   * @param context - an AudioContext instance
   * @param config - SFDriverConfig object with path settings for music
   * symbols and SoundFonts. See class for requirements info.
   * @param instrument the default instrument - if null, the first instrument 
   * in InstrumentUtils will be used.
   * @param loadSFBankImmediately - if true, the sfGmBank specified in the
   * SFDriverConfig is loaded as the [SFScoreDriver] is created.
   * @returns a Promise that completes with a new SFScoreDriver instance.*/
  static async create(pixiAppHelper, context, config) {
    let sfd = new _SFScoreDriver();
    sfd._pah = pixiAppHelper;
    sfd._context = context;
    let hasAudio = sfd._context != null;
    if (!hasAudio && config.throwErrorOnNoAudioContext)
      throw "AudioContext not set";
    sfd._drawManager = new SFDriverDrawManager(sfd);
    if (hasAudio) {
      sfd._outputComponent = config.outputComponent ?? new DestinationAudioComponent2(context);
      sfd._slm = SoundLibManager6.get(
        context,
        config.soundLibType,
        sfd._outputComponent
      );
      sfd._slm.soundLibEvent.on(sfd._onSoundLibEvent);
    }
    sfd._statusEmitter.emit(new SFDriverStatusEvent(
      0 /* textureLoading */
    ));
    await MusicTextures3.createTextures(config.initialPixelsPerSpace);
    sfd._renderer = new BitmapMusicRenderer2(pixiAppHelper);
    if (hasAudio) {
      if (!MediaDeviceManager4.isInitialized) {
        let deviceManager = MediaDeviceManager4.get();
        await deviceManager.init();
      }
      sfd._micManager = MicManager4.get(sfd._context);
      sfd._audioRecorder = new AudioRecorder(sfd._micManager);
      sfd._audioRecorder2 = new AudioRecorder22(sfd._micManager);
      sfd._useAudioRecorder2 = config.useAudioRecorder2;
    }
    sfd.setInstrument(config.initialInstrument);
    if (config.soundMapPath != null && config.loadMapOnInit && hasAudio) {
      await sfd.loadSoundMap(config.soundMapPath);
      if (config.loadPresetsOnInit) {
        await sfd.loadPresets(null);
      }
    }
    return sfd;
  }
  /**attempts to enable microphone, if one is available. User is prompted
   * to give permission the first session.*/
  async enableMicrophone(micOptions = null, videoOptions = null) {
    let deviceManager = MediaDeviceManager4.get();
    let useCamera = videoOptions != null;
    await deviceManager.getPermissions(true, useCamera);
    await deviceManager.getDevices();
    if (deviceManager.currentMic == null) {
      deviceManager.setMic();
    }
    if (useCamera && deviceManager.currentCamera == null) {
      deviceManager.setCamera();
    }
    await this._micManager.initMicMediaStream(micOptions, videoOptions);
  }
  unloadScore() {
    if (this._score == null) {
      return;
    }
    if (this._assessor != null) {
      this._assessor.prepareForRemoval();
      this._assessor = null;
    }
    if (this._scoreViewer != null) {
      this._scoreViewer.prepareForRemoval();
      this._scoreViewer.parent?.removeChild(this._scoreViewer);
      this._scoreViewer = null;
    }
    this._scoreViewer = null;
    this._score = null;
    this.destroyParser();
  }
  parseScore(musicXmlString, computeMaxKeySizeForRendering = true, overrideScoreProps = false, scoreProps = null, keepParserObject = false) {
    return new Promise((resolve, reject) => {
      let parser = new MusicXmlParser22();
      this._statusEmitter.emit(new SFDriverStatusEvent(
        3 /* scoreParsing */
      ));
      parser.parseXML(
        musicXmlString,
        computeMaxKeySizeForRendering,
        overrideScoreProps,
        scoreProps
      ).then((score) => {
        if (keepParserObject) {
          this._parser = parser;
        }
        resolve(score);
      }).catch((e) => {
        reject(e);
      });
    });
  }
  destroyParser() {
    this._parser = null;
  }
  renderNotation(score, width, height, parts = null, measuresPerSystem = 0, clearSystemIndents = false) {
    this._score = score;
    this._statusEmitter.emit(new SFDriverStatusEvent(
      4 /* scoreRendering */
    ));
    return this._drawManager.renderNotation(
      width,
      height,
      parts,
      measuresPerSystem,
      clearSystemIndents
    );
  }
  /**
   * re-renders the score and adds the new VisualScore to the existing 
   * ScoreViewer
   */
  resetNotation(width = 0, height = 0, measuresPerSystem = 0, parts = null) {
    this._drawManager.resetNotation(
      width,
      height,
      measuresPerSystem,
      parts
    );
  }
  /**
   * resizes the scoreViewer visible area - if values are left null, 
   * existing width and/or height is used
   */
  resizeScoreViewer(width, height) {
    this._drawManager.resizeScoreViewer(width, height);
  }
  /**Loads a sound map json file. See the [SoundMap.fromJson] constructor for
   * details on the file and folder structures.*/
  async loadSoundMap(mapUrl) {
    await this._slm.loadSoundMap(mapUrl);
  }
  /**Loads the presets from the map. The [bankPresetPairs] is a list in the
   * form of [bank, preset, bank, preset, ...]. If left null, all presets
   * in the map are loaded.*/
  async loadPresets(bankPresetPairs) {
    await this._slm.loadPresets(bankPresetPairs);
  }
  /**Adds SequencerSound objects to the AudioSequencer*/
  addSequencerSounds(sounds) {
    if (!this.audioSupported)
      throw "Audio support not available";
    if (this._audioSequencer == null) {
      this._audioSequencer = new AudioSequencer(this._context);
    }
    for (let sound of sounds) {
      this._audioSequencer.addSound(sound);
    }
  }
  preparePlayback(pdo) {
    if (!this.audioSupported)
      throw "Audio support not available";
    this._prepareSFAudio(pdo);
    if (pdo.instDO == null) {
      pdo.instDO = this._instrument;
    } else {
      this.setInstrument(pdo.instDO);
    }
    this._prepareAssessment(pdo.assessmentType);
    if (pdo.playAudio && this._audioSequencer != null) {
      pdo._startNoteTimeOffset = pdo.startNG != null ? this._score.playbackInfo.convertQNoteTimeToSeconds(
        pdo.startNG.qNoteTime
      ) : 0;
    }
  }
  play(playbackCompleteCallback, pdo) {
    if (this._slm != null && this._slm.isPlaying) {
      return;
    }
    if (!this.audioSupported)
      throw "Audio support not available";
    this.preparePlayback(pdo);
    this.playPrepared(pdo, playbackCompleteCallback);
  }
  playPrepared(pdo, playbackCompleteCallback) {
    if (this._slm != null && this._slm.isPlaying) {
      return;
    }
    if (!this.audioSupported)
      throw "Audio support not available";
    this._slm.clearNotes();
    this._slm.prepareSequencedPlayback();
    this._playbackCompleteCallback = playbackCompleteCallback;
    this._isPlaying = true;
    this._slm.play();
    if (pdo.playAudio && this._audioSequencer != null) {
      this._audioSequencer.play(
        pdo._countoffDuration,
        pdo._startNoteTimeOffset
      );
    }
    this._scoreViewer.play(
      this._performanceNoteMaker.sampleTimeStamps,
      pdo._beatQNotePositions,
      pdo._countoffBeatQNotePositions,
      0,
      pdo.startNG,
      pdo.endQNoteTime
    );
    if (!this._micManager.isCapturing && (pdo.record && !this._useAudioRecorder2 || this._assessor != null && this._assessor.requiresMicrophone)) {
      this._micManager.startCapture();
    }
    if (pdo.record) {
      if (this._useAudioRecorder2) {
        this._audioRecorder2.record();
      } else {
        this._audioRecorder.record(false);
      }
    }
    if (this._assessor != null)
      this._assessor.start();
  }
  stop() {
    if (this._slm != null) {
      this._slm.stop();
      if (this._audioSequencer != null) {
        this._audioSequencer.stop();
      }
      this._scoreViewer.stop();
      if (!this._leaveMicCaptureEnabled) {
        this._micManager.stopCapture();
      }
      if (this._assessor != null) {
        this._assessor.stop();
      }
      if (this._audioRecorder.isRecording) {
        this._audioRecorder.stop(false);
      }
      if (this._audioRecorder2.isRecording) {
        this._audioRecorder2.stop();
      }
    }
    this._isPlaying = false;
  }
  setInstrument(instDO) {
    if (instDO != null) {
      this._instrument = instDO;
    }
  }
  getAssessmentScore() {
    return this._assessor?.getScore();
  }
  getAssessmentResults() {
    return this._assessor?.getResults();
  }
  clearAssessmentVisuals() {
    if (this._assessor != null) {
      this._assessor.clearAssessmentVisuals();
    }
  }
  /**Gets the recorded audio as an AudioBuffer. Does not use AudioRecorder2.
   * @stereo - if true, mono recording is copied to both channels
   * @resampleRate - default null means no resampling. If specified, must
   * be a value lower than AudioContext's sample rate.
   * @deleteRecordedAudio - clears audio data from this class so it can be
   * garbage collected.*/
  getRecording(stereo, resampleRate = null, deleteRecordedAudio = true) {
    if (!this.audioSupported)
      throw "Audio support not available";
    return this._audioRecorder.getAudioBuffer(
      stereo,
      resampleRate,
      deleteRecordedAudio
    );
  }
  /**gets an html.Blob object that can be saved to a file. This does not use
   * AudioRecorder2*/
  getRecordingAsOggAudioBlob() {
    if (!this.audioSupported)
      throw "Audio support not available";
    return this._audioRecorder.getOggAudioBlob();
  }
  /**gets recording as an html.Blob object that can be saved to a file. This
   * uses AudioRecorder2.*/
  getRecordingBlobAR2() {
    if (!this.audioSupported)
      throw "Audio support not available";
    return this._audioRecorder2.getAudioBlob();
  }
  /**returns the AudioContext time, in seconds, when recording starts.*/
  get recordStartTime() {
    if (this._audioRecorder2 != null) {
      return this._audioRecorder2.recordStartTime;
    } else if (this._audioRecorder != null) {
      return this._audioRecorder.recordStartTime;
    } else {
      return null;
    }
  }
  /**returns the AudioContext time, in seconds, when recording stops.*/
  get recordStopTime() {
    if (this._audioRecorder2 != null) {
      return this._audioRecorder2.recordStopTime;
    } else if (this._audioRecorder != null) {
      return this._audioRecorder.recordStopTime;
    } else {
      return null;
    }
  }
  /**Gets an AudioElement with the recorded audio. This uses AudioRecorder2.*/
  getAudioElementAR2() {
    if (!this.audioSupported)
      throw "Audio support not available";
    return this._audioRecorder2.getAudioElement();
  }
  startBeatTrackPlayback(playbackCompleteCallback, playSoundFontNotes = true, countoffBeats = null, assessmentType = 0 /* none */) {
    if (!this.audioSupported)
      throw "Audio support not available";
    if (this._scoreViewer == null) {
      throw "must call init() and render a score before playback.";
    }
    this._prepareAssessment(assessmentType);
    this._playbackCompleteCallback = playbackCompleteCallback;
    if (this._assessor != null)
      this._assessor.start();
  }
  _prepareSFAudio(pdo) {
    if (this._score == null) {
      throw "must call init() and render a score before playback.";
    }
    if (this._performanceNoteMaker == null) {
      this._performanceNoteMaker = new SFPerformanceNoteMaker(this._slm);
    }
    if (pdo.countoffBeats == null) {
      if (pdo.countoffSeconds != null) {
        pdo.countoffBeats = this._getCountoffBeatsForSeconds(
          pdo.countoffSeconds,
          pdo.startNG,
          pdo.tempoRatio
        );
      } else {
        pdo.countoffBeats = 0;
      }
    }
    this._performanceNoteMaker.addNotesInParts(
      this._score,
      this._score.parts,
      {
        tempoRatio: pdo.tempoRatio,
        playSoundFontNotes: pdo.playSoundFontNotes,
        metronomeVolume: pdo.metronomeVolume,
        startNG: pdo.startNG,
        endQNoteTime: pdo.endQNoteTime,
        startRepeatPass: 1,
        endRepeatPass: null,
        numCountoffBeats: pdo.countoffBeats,
        playRepeats: pdo.playRepeats
      }
    );
    pdo._countoffDuration = this._performanceNoteMaker.countoffDuration;
    pdo._beatQNotePositions = this._performanceNoteMaker.beatQNotePositions;
    pdo._countoffBeatQNotePositions = this._performanceNoteMaker.countoffBeatQNotePositions;
    return pdo;
  }
  _getCountoffBeatsForSeconds(seconds, startNG, tempoRatio) {
    if (startNG == null) {
      startNG = this._score.parts[0].staves[0].measures[0].voices[0].noteGroups[0];
    }
    let stack = startNG.voice.measure.stack;
    let tm;
    let tmList = stack.tempoMarkers;
    if (tmList != null) {
      for (let i = tmList.length - 1; i >= 0; i--) {
        if (tmList[i].qNoteTime <= startNG.qNoteTime) {
          tm = tmList[i];
        }
      }
    } else {
      tm = stack.getInheritedTempoMarker();
    }
    let tempo = tm != null ? tm.tempo : 90;
    let actualTempo = tempo * tempoRatio;
    let startMeasure = startNG.voice.measure;
    let beatType = startMeasure.beatType;
    let qNotesPerMeasure = startMeasure.numBeats * 4 / beatType;
    let secondsPerMeasure = qNotesPerMeasure * 60 / actualTempo;
    let numCountoffMeasures = Math.ceil(seconds / secondsPerMeasure);
    seconds = numCountoffMeasures * secondsPerMeasure;
    let qNotesPerCountoffBeat;
    if (beatType % 8 === 0 && actualTempo >= SFPerformanceNoteMaker.COMPOUND_METER_TEMPO_CUTOFF) {
      qNotesPerCountoffBeat = 3 * (4 / beatType);
    } else {
      qNotesPerCountoffBeat = 4 / beatType;
    }
    let numBeats = seconds / (qNotesPerCountoffBeat * (60 / actualTempo));
    return Math.round(numBeats);
  }
  _prepareAssessment(assessType) {
    this._assessor = this._assessorFactory.getAssessor(
      assessType,
      this.scoreViewer
    );
    if (this._assessor == null) {
      return;
    }
    if (this._assessor.requiresMicrophone && !MediaDeviceManager4.get().isMicReady) {
      throw "Microphone device not set up.";
    }
    this._assessor.setInstrument(this._instrument);
  }
  get pixiAppHelper() {
    return this._pah;
  }
  get score() {
    return this._score;
  }
  get parser() {
    return this._parser;
  }
  get scoreViewer() {
    return this._scoreViewer;
  }
  get isPlaying() {
    return this._isPlaying;
  }
  get instrument() {
    return this._instrument;
  }
  /**default is false. If true, ogg encoding happens in real time as date is
   * received. Could create performance issues (but seems pretty fast)*/
  get encodeLive() {
    return this._audioRecorder != null && this._audioRecorder.encodeLive;
  }
  set encodeLive(value) {
    if (!this.audioSupported)
      throw "Audio support not available";
    this._audioRecorder.encodeLive = value;
  }
  /**If true, when playback is stopped, no call will be sent to the 
   * [MicManager] to stop mic capture.*/
  get leaveMicCaptureEnabled() {
    return this._leaveMicCaptureEnabled;
  }
  set leaveMicCaptureEnabled(value) {
    this._leaveMicCaptureEnabled = value;
  }
  /**AudioRecorder2 uses MediaRecorder and creates a blob object.*/
  get useAudioRecorder2() {
    return this._useAudioRecorder2;
  }
  set useAudioRecorder2(value) {
    this._useAudioRecorder2 = value;
  }
  /**Returns the countoff duration in seconds, or null if no playback
   * has occurred.*/
  get countoffDuration() {
    return this._performanceNoteMaker?.countoffDuration;
  }
  /**Gets a reference to the audioSequencer. If it doesn't exist, it is
   * initialized.*/
  get audioSequencer() {
    if (!this.audioSupported)
      throw "Audio support not available";
    if (this._audioSequencer == null) {
      this._audioSequencer = new AudioSequencer(this._context);
    }
    return this._audioSequencer;
  }
  /**Affects the number of measures that fit on a line, effectively adjusting
   * the scaling. The actual width of the rendered music is controlled
   * separately. Default is ScoreProperties.DEFAULT_PAGE_WIDTH.*/
  get defaultPageWidth() {
    return this._drawManager.defaultPageWidth;
  }
  set defaultPageWidth(value) {
    this._drawManager.defaultPageWidth = value;
  }
  /**indicates whether the driver has audio support for playback, assessment,
   * recording.*/
  get audioSupported() {
    return this._context != null;
  }
  /**Gets/Sets the factory used to create [Assessor]s*/
  get assessorFactory() {
    return this._assessorFactory;
  }
  set assessorFactory(value) {
    this._assessorFactory = value;
  }
  /**The audio output from the [SFScoreDriver] will be routed through the
   * [outputComponent]. This defaults to an instance of [MasterAudioComponent]
   * and can be changed via the [SFDriverConfig] during initialization.*/
  get outputComponent() {
    return this._outputComponent;
  }
};

// src/score_performance/drivers/sf_score_driver/SFDriverConfig.ts
import { ScoreProperties as ScoreProperties6, InstrumentUtils as InstrumentUtils2 } from "score_data";
var SFDriverConfig = class {
  /**/
  /////////////////////////////////////////*/
  /**The [soundLibType] should match the setting in your map.json file.
   * If [loadMapOnInit] is true and a [soundMapPath] is specified, the map
   * will be loaded during initialization (and completion of initialization
   * will be delayed until this completes). If [loadPresetsOnInit] is also
   * true, all presets in the sound map will be loaded during initialization.
   * */
  constructor(soundLibType, {
    soundMapPath = null,
    loadMapOnInit = true,
    loadPresetsOnInit = false
  }) {
    /**The initial scaling for the score. Used for initial texture generation.
     * If you pass in a [ScoreProperties] object when parsing your score, its
     * [ScoreProperties.pixelsPerInch] property should match this.*/
    this.initialPixelsPerSpace = ScoreProperties6.DEFAULT_PIXELS_PER_SPACE;
    /**if true, AudioRecorder2 will be used for recording audio. This uses
     * MediaRecorder.*/
    this.useAudioRecorder2 = false;
    /**if no AudioContext is supplied, either an error will be thrown, or the
     * driver will be initialized with no playback/assessment/recording support.
     * */
    this.throwErrorOnNoAudioContext = true;
    /**the initial instrument to use.*/
    this.initialInstrument = InstrumentUtils2.instruments[0];
    this.soundLibType = soundLibType;
    this.soundMapPath = soundMapPath;
    this.loadMapOnInit = loadMapOnInit;
    this.loadPresetsOnInit = loadPresetsOnInit;
  }
};
export {
  AssessmentBeat,
  AssessmentNote,
  AssessmentResults,
  AssessmentType,
  AssessmentVisualizer,
  AssessmentVisualizerBase,
  Assessor,
  BasicAssessorFactory,
  FreeTimeRhythmAssessor,
  IAssessorFactory,
  InteractionSettings,
  NewAssessmentVisualizer,
  PitchAssessor,
  RhythmAssessor,
  RhythmAssessorHitMode,
  SFDriverConfig,
  SFDriverStatusEvent,
  SFDriverStatusType,
  SFLoopDriver,
  SFLoopDriverConfig,
  SFPerformanceNoteMaker,
  SFPlaybackDO,
  SFScoreDriver,
  ScoreViewer3 as ScoreViewer,
  ScoreViewerEvent,
  ScoreViewerEventType,
  ScoreViewerPlaybackManager,
  ScoreViewerViewManager,
  TimeTrackMode
};
